// Template Engine
class TemplateEngine {
  static render(template, data, html = false) {
    let result = template;
    Object.keys(data).forEach((key) => {
      const regex = new RegExp(`{#\\s*${key}\\s*#}`, "g");
      result = result.replace(regex, data[key] || "");
    });

    if (html) {
      return result.trim();
    }

    const templateElement = document.createElement("template");
    templateElement.innerHTML = result.trim();

    return templateElement.content.cloneNode(true);
  }
}

class PIWIDropdownMenu extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector("[data-dropdown-toggle]");
    this.label = this.querySelector("[data-dropdown-label]");
    this.menu = this.querySelector("[data-dropdown-menu]");
    this.items = this.querySelectorAll("[data-dropdown-item]");
    this.isOpen = false;
    this.selectedValue = null;
    this.subscriptionConsent = this.querySelector(
      "[data-subscription-consent]"
    );

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleItemClick = this.handleItemClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    if (!this.toggle || !this.menu) return;
    this.toggle.addEventListener("click", this.handleToggleClick);

    this.items.forEach((item) => {
      item.addEventListener("click", this.handleItemClick);
    });

    document.addEventListener("click", this.handleClickOutside);

    this.subscriptionConsent.addEventListener(
      "change",
      this.handleSubscriptionConsentChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.toggle.removeEventListener("click", this.handleToggleClick);
    this.items.forEach((item) => {
      item.removeEventListener("click", this.handleItemClick);
    });
    document.removeEventListener("click", this.handleClickOutside);
  }

  handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  handleItemClick(event) {
    event.preventDefault();
    this.selectedValue = parseInt(event.target.dataset.value);
    this.items.forEach((item) => {
      if (parseInt(item.dataset.value) == this.selectedValue) {
        item.setAttribute("data-selected", "");
        this.label.innerHTML = item.innerHTML;
      } else {
        item.removeAttribute("data-selected");
      }
    });
    if (this.subscriptionConsent.checked) {
      this.closeDropdown();
    }

    this.removeAttribute("data-focus");
  }

  handleClickOutside(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  openDropdown() {
    this.isOpen = true;
    this.toggle.setAttribute("aria-expanded", "true");
    this.menu.classList.add("pi-subscription-frequency-dropdown__menu--open");
    this.setAttribute("data-open", "");
  }

  closeDropdown() {
    this.isOpen = false;
    this.toggle.setAttribute("aria-expanded", "false");
    this.menu.classList.remove(
      "pi-subscription-frequency-dropdown__menu--open"
    );
    this.removeAttribute("data-open");
  }

  handleSubscriptionConsentChange() {
    if (this.subscriptionConsent.checked) {
      this.removeAttribute("data-focus-consent");
    } else {
      this.setAttribute("data-focus-consent", "");
    }
  }

  _focus() {
    if (this.selectedValue == null) {
      this.setAttribute("data-focus", "");
    }
    setTimeout(() => {
      if (!this.isOpen) {
        this.toggle.click();
      }
    }, 100);
    if (!this.subscriptionConsent.checked) {
      this.setAttribute("data-focus-consent", "");
    }
  }
}

customElements.define("pi-dropdown-menu", PIWIDropdownMenu);

class WIQPurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.purchaseOptions = this.querySelectorAll("[data-purchase-option]");
    this.handleClick = this.handleClick.bind(this);
    this.selectedOption = this.dataset.purchaseActive || null;
  }
  connectedCallback() {
    this.purchaseOptions.forEach((option) => {
      option.addEventListener("click", this.handleClick);
    });
  }
  disconnectedCallback() {
    this.purchaseOptions.forEach((option) => {
      option.removeEventListener("click", this.handleClick);
    });
  }
  handleClick(event) {
    event.preventDefault();
    this.selectedOption = event.target.dataset.purchaseOption;
    this.setAttribute("data-purchase-active", this.selectedOption);
    this._dispatchEvent();
  }

  switchOption(option) {
    this.selectedOption = option;
    this.setAttribute("data-purchase-active", this.selectedOption);
    this._dispatchEvent();
  }

  _dispatchEvent() {
    this.dispatchEvent(
      new CustomEvent("wiq:purchase-options:change", {
        detail: {
          purchaseOption: this.selectedOption,
        },
      })
    );
  }
}
customElements.define("wiq-purchase-options", WIQPurchaseOptions);

/* =============================== Quantity Input =============================== */

class WIQQuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.addButton = this.querySelector("button[data-add-to-bundle]");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button:not([data-add-to-bundle])").forEach(
      (button) =>
        button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
    if (this.addButton) {
      this.addButton.addEventListener(
        "click",
        this.onAddButtonClick.bind(this)
      );
    }
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector("button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector("button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }

  getValue() {
    return parseInt(this.input.value);
  }

  onAddButtonClick(event) {
    event.preventDefault();
    this.input.value = 1;
    this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define("wiq-quantity-input", WIQQuantityInput);

class WIQBundleItem extends HTMLElement {
  constructor() {
    super();
    this.properties = null;
    this.variantId = null;
    this.inputQuantity = this.querySelector("wiq-quantity-input");
    this.template = this.querySelector("template");
    this.onInputChange = this.onInputChange.bind(this);
    this.modalOpeners = this.querySelectorAll("[data-open-modal]");
    this.modal = document.querySelector("wiq-bundle-item-modal");
    if (this.hasAttribute("data-gift-card")) {
      this.productVariants = JSON.parse(
        this.querySelector("[data-product-variants]").textContent
      );
    }
  }

  connectedCallback() {
    this.inputQuantity.addEventListener("change", this.onInputChange);
    this.modalOpeners.forEach((opener) => {
      opener.addEventListener("click", this.openModal.bind(this));
      opener.addEventListener("mouseenter", this.onHover.bind(this));
    });
  }
  
  disconnectedCallback() {
    this.inputQuantity.removeEventListener("change", this.onInputChange);
  }

  onInputChange() {
    const value = this.inputQuantity.getValue();
    if (value > 0) {
      this.setAttribute("data-active", "");
    } else {
      this.removeAttribute("data-active");
    }
    this.dispatchEvent();
  }

  // FIXED dispatchEvent method to include variant and properties for gift cards
  dispatchEvent() {
    const eventDetail = {
      productId: this.dataset.productId,
      quantity: this.inputQuantity.getValue(),
    };

    // If it's a gift card, include the stored variant and properties
    if (this.hasAttribute("data-gift-card")) {
      if (this.variantId) {
        eventDetail.variantId = this.variantId;
      }
      if (this.properties) {
        eventDetail.properties = this.properties;
      }
    }

    document.dispatchEvent(
      new CustomEvent("wiq:bundle:change", {
        detail: eventDetail
      })
    );
  }

  openModal(event) {
    event.preventDefault();
    this.modal.show({
      opener: event.target,
      handle: this.dataset.productHandle,
      quantity: this.inputQuantity.getValue(),
    });
  }

  onHover() {
    this.modal.onHover(this.dataset.productHandle);
  }

  getPrice(variantId) {
    this.dataset.variantId = variantId;
    if (this.productVariants) {
      const variant = this.productVariants.find(
        (variant) => variant.id == variantId
      );
      this.dataset.price = variant.price;
      return variant.price;
    }
    return this.dataset.price;
  }

  getHtml(quantity, price, uniqueId) {
    const clone = this.template.content.cloneNode(true);
    const card = clone.querySelector("[html]");
    const input = card.querySelector("wiq-quantity-input input");
    const priceEl = card.querySelector("[data-price]");
    
    // Generate a unique identifier for this specific item instance
    const itemId = uniqueId || `${this.dataset.productId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    input.setAttribute("value", quantity);
    
    // Add unique identifier and gift card data to the root element
    const rootElement = card.querySelector("wiq-bundle-selected-item");
    if (rootElement) {
      rootElement.setAttribute("data-item-id", itemId);
      rootElement.setAttribute("data-product-id", this.dataset.productId);
      
      if (this.hasAttribute("data-gift-card")) {
        rootElement.setAttribute("data-gift-card", "true");
        if (this.variantId) {
          rootElement.setAttribute("data-variant-id", this.variantId);
        }
        if (this.properties) {
          rootElement.setAttribute("data-properties", JSON.stringify(this.properties));
        }
      }
    }
    
    // Get bundle builder to check for discounts
    const bundleBuilder = document.querySelector("wiq-bundle-builder");
    const currentDiscount = bundleBuilder.loop.getCurrentDiscount(bundleBuilder.totalCount);
    const subscriptionActive = bundleBuilder.purchaseOptions.selectedOption === "subscription";
    
    // Calculate prices (rest remains the same)
    const originalPrice = price;
    let finalPrice = originalPrice;
    let hasDiscount = false;
    
    if (currentDiscount && !this.hasAttribute("data-gift-card")) {
        const volumeDiscountAmount = Math.round(originalPrice * (currentDiscount.value / 100));
        finalPrice = originalPrice - volumeDiscountAmount;
        hasDiscount = true;
    }
    
    if (subscriptionActive && bundleBuilder.subscriptionPercentage > 0 && !this.hasAttribute("data-gift-card")) {
        const subscriptionDiscountAmount = Math.round(finalPrice * bundleBuilder.subscriptionPercentage);
        finalPrice = finalPrice - subscriptionDiscountAmount;
        hasDiscount = true;
    }
    
    if (hasDiscount) {
        priceEl.innerHTML = `
            <div class="price-stack">
                <div class="original-price">${Shopify.formatMoney(originalPrice)}</div>
                <div class="discounted-price">${Shopify.formatMoney(finalPrice)}</div>
            </div>
        `;
    } else {
        priceEl.innerHTML = Shopify.formatMoney(finalPrice);
    }
    
    return card.innerHTML;
  }
}

customElements.define("wiq-bundle-item", WIQBundleItem);

/* ======================
 *   Bundle Modal Item
 * ====================== */

class WIQBundleModalItem extends WIQBundleItem {
  constructor() {
    super();
    this.template = null;
  }
}

customElements.define("wiq-bundle-modal-item", WIQBundleModalItem);

/* ======================
 *   Bundle Selected Item
 * ====================== */

class WIQBundleSelectedItem extends HTMLElement {
  constructor() {
    super();
    this.inputQuantity = null;
    this.removeBtn = null;
    this.onInputChange = this.onInputChange.bind(this);
    this.onRemoveBtnClick = this.onRemoveBtnClick.bind(this);
  }
  
  connectedCallback() {
    // Find elements after connection
    this.inputQuantity = this.querySelector("wiq-quantity-input");
    this.removeBtn = this.querySelector("button[data-remove-item]");
    
    // Read gift card data from attributes
    this.productId = this.getAttribute("data-product-id");
    this.isGiftCard = this.hasAttribute("data-gift-card");
    
    if (this.isGiftCard) {
      this.variantId = this.getAttribute("data-variant-id");
      const propertiesStr = this.getAttribute("data-properties");
      if (propertiesStr) {
        try {
          this.properties = JSON.parse(propertiesStr);
        } catch (e) {
          this.properties = null;
        }
      }
    }
    
    // Set up event listeners
    if (this.inputQuantity) {
      this.inputQuantity.addEventListener("change", this.onInputChange);
    }
    
    if (this.removeBtn) {
      this.removeBtn.addEventListener("click", this.onRemoveBtnClick);
    }
  }
  
  disconnectedCallback() {
    if (this.inputQuantity) {
      this.inputQuantity.removeEventListener("change", this.onInputChange);
    }
    if (this.removeBtn) {
      this.removeBtn.removeEventListener("click", this.onRemoveBtnClick);
    }
  }
  
  onInputChange() {
    if (!this.inputQuantity) return;
    const value = this.inputQuantity.getValue();
    this.dispatchBundleEvent(value);
  }
  
  onRemoveBtnClick(event) {
    event.preventDefault();
    this.dispatchBundleEvent(0);
  }
  
  dispatchBundleEvent(quantity) {
    const eventDetail = {
      productId: this.productId,
      quantity: quantity,
    };
    
    if (this.isGiftCard) {
      if (this.variantId) {
        eventDetail.variantId = this.variantId;
      }
      if (this.properties) {
        eventDetail.properties = this.properties;
      }
    }
    
    document.dispatchEvent(
      new CustomEvent("wiq:bundle:change", {
        detail: eventDetail
      })
    );
  }
  
  getValue() {
    return this.inputQuantity ? this.inputQuantity.getValue() : 0;
  }
}

// Make sure to define this as a custom element
customElements.define("wiq-bundle-selected-item", WIQBundleSelectedItem);

/* ======================
 *   Bundle Item Modal
 * ====================== */

class WIQBundleItemModal extends HTMLElement {
  constructor() {
    super();
    this.cache = {};
    this.handle = null;
    this.init(true);
  }

  init(bool = false) {
    this.querySelector("[data-close-modal]")?.addEventListener(
      "click",
      this.hide.bind(this, false)
    );

    if (bool) {
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });

      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
    this.initSwiper();
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  initSwiper() {
    if (!this.handle) return;
    const swiper = new Swiper(`[data-swiper="${this.handle}"]`, {
      loop: true,
      initialSlide: 2,
      pagination: {
        el: ".gallery__pagination",
        clickable: true,
      },
      effect: "slide",
      speed: 800,
    });
  }

  async show({ opener, handle, quantity }) {
  this.handle = handle;
  opener.setAttribute("data-loading", "");
  opener.setAttribute("disabled", "");

  const html = this.cache[handle] || (await this.fetchProduct(handle));
  const dom = new DOMParser().parseFromString(html, "text/html");

  // Set quantity if provided
  const quantityInput = dom.querySelector("wiq-quantity-input input");
  if (quantityInput && quantity > 0) {
    quantityInput.setAttribute("value", quantity);
    const modalItem = dom.querySelector("wiq-bundle-modal-item");
    if (modalItem) {
      modalItem.setAttribute("data-active", "");
    }
  }

  // Update the modal content
  this.innerHTML = dom.querySelector(".shopify-section").innerHTML;
  
  // Show modal first (Judge.me won't hydrate while hidden)
  document.body.classList.add('overflow-hidden');
  this.setAttribute('data-modal-active', '');

  // Initialize other modal functionality
  this.init();
  
  // Wait a frame to ensure DOM is rendered, then reload Judge.me widgets
  requestAnimationFrame(() => {
    // Call Judge.me cache server reload function
    if (window.jdgmCacheServer && typeof window.jdgmCacheServer.reloadAll === 'function') {
      window.jdgmCacheServer.reloadAll();
    } else {
      console.warn('Judge.me cache server not available. Make sure cache server scripts are loaded.');
    }
  });

  // Track product view
  if (handle) {
    fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        const productId = product.id.toString();
        const productPrice = (product.price / 100).toFixed(2);

        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'Viewed Product',
            'ecommerce': {
              'detail': {
                'products': [{
                  'id': productId,
                  'name': product.title,
                  'price': productPrice,
                  'brand': product.vendor || '',
                  'category': product.type || '',
                  'variant': product.variants[0]?.id.toString() || ''
                }]
              }
            }
          });
        }

        // Meta previously received nothing when a customer opened a product's
        // "View More Info" modal - only the dataLayer push above existed, under
        // a custom event name no standard GA4/Meta integration listens for. Call
        // fbq directly, matching how AddToCart is fired in trackItemAddedToBundle,
        // so ViewContent lands even with no tag manager involvement.
        if (typeof fbq !== 'undefined') {
          fbq('track', 'ViewContent', {
            content_ids: [productId],
            content_name: product.title,
            content_type: 'product',
            value: parseFloat(productPrice),
            currency: 'USD'
          });
        }

        // GA4's standard product-view event. As with add_to_cart, the dataLayer
        // push above has no consumer on this store (Google tag, not a Tag
        // Manager container), so call gtag directly.
        if (typeof gtag === 'function') {
          gtag('event', 'view_item', {
            currency: 'USD',
            value: parseFloat(productPrice),
            items: [{
              item_id: productId,
              item_name: product.title,
              price: parseFloat(productPrice),
              quantity: 1
            }]
          });
        }

        // Klaviyo. The onsite embed is loaded storewide, so _learnq is available
        // here, but nothing in the bundle builder had ever called it - Klaviyo
        // received no signal at all from this page. "Viewed Product" is Klaviyo's
        // own standard metric name (QwSdEh), which browse-abandonment flows and
        // product-level segments key off. trackViewedItem additionally feeds
        // Klaviyo's "recently viewed items" block in emails.
        if (window._learnq && typeof window._learnq.push === 'function') {
          const productUrl = product.url
            ? `${window.location.origin}${product.url}`
            : `${window.location.origin}/products/${handle}`;
          const imageUrl = product.featured_image
            ? (product.featured_image.startsWith('http') ? product.featured_image : `https:${product.featured_image}`)
            : '';

          const klaviyoItem = {
            ProductName: product.title,
            ProductID: productId,
            SKU: product.variants?.[0]?.sku || '',
            Categories: product.tags || [],
            ImageURL: imageUrl,
            URL: productUrl,
            Brand: product.vendor || '',
            Price: parseFloat(productPrice),
            CompareAtPrice: product.compare_at_price
              ? parseFloat((product.compare_at_price / 100).toFixed(2))
              : null
          };

          window._learnq.push(['track', 'Viewed Product', klaviyoItem]);
          window._learnq.push(['trackViewedItem', {
            Title: product.title,
            ItemId: productId,
            Categories: product.tags || [],
            ImageUrl: imageUrl,
            Url: productUrl,
            Metadata: {
              Brand: product.vendor || '',
              Price: parseFloat(productPrice),
              CompareAtPrice: klaviyoItem.CompareAtPrice
            }
          }]);
        }
      })
      .catch(error => {
        console.error('Error tracking product view:', error);
      });
  }

  if (opener) {
    opener.removeAttribute("disabled");
    opener.removeAttribute("data-loading");
  }
}

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("data-modal-active");
    
    // Clean up Judge.me event listeners and widgets
    if (window.jdgm) {
      const widgets = this.querySelectorAll('.jdgm-widget');
      widgets.forEach(widget => {
        // Remove Judge.me's event listeners
        const newWidget = widget.cloneNode(false);
        widget.parentNode.replaceChild(newWidget, widget);
      });
    }
    
    setTimeout(() => {
      this.innerHTML = "";
    }, 200);
  }

  fetchProduct(handle) {
    return fetch(`/products/${handle}/sections?section_id=pi-bundle-modal`)
      .then((response) => response.text())
      .then((html) => {
        this.cache[handle] = html;
        return html;
      });
  }

  onHover(handle) {
    if (this.cache[handle]) return;
    const html = this.fetchProduct(handle);
  }
}
customElements.define("wiq-bundle-item-modal", WIQBundleItemModal);

class LocalBundlePricing {
  constructor(shippingThreshold) {
    // Hardcode the Appstle UI tiers here so the frontend matches the checkout
    this.discountList = [
      { conditionType: "volume", value: 5, minCartQuantity: 24 },
      { conditionType: "volume", value: 10, minCartQuantity: 36 },
      { conditionType: "shipping", value: 0, minCartQuantity: shippingThreshold }
    ].sort((a, b) => b.minCartQuantity - a.minCartQuantity);
    
    this.highestThreshold = Math.max(
      ...this.discountList.map((d) => d.minCartQuantity || 0)
    );
  }

  getCurrentDiscount(totalCount, includeShipping = false) {
    return this.discountList.find(
      (discount) =>
        totalCount >= discount.minCartQuantity &&
        (includeShipping ? true : discount.conditionType !== "shipping")
    );
  }

  getClosestDiscount(totalCount, includeShipping = false) {
    const discounts = [...this.discountList]
      .filter((discount) =>
        includeShipping ? true : discount.conditionType !== "shipping"
      )
      .sort((a, b) => a.minCartQuantity - b.minCartQuantity);

    for (let i = 0; i < discounts.length; i++) {
      if (totalCount <= discounts[i].minCartQuantity) {
        return discounts[i];
      }
    }
    return discounts[discounts.length - 1] || null;
  }
}

class WIQBundleFilters extends HTMLElement {
  constructor() {
    super();
    this.url = new URL(window.location);
    this.selectedCollection = null;
    this.ingredients = [];
    this.negativeIngredients = [];
    this.collectionButtons = this.querySelectorAll("[data-product-type]");
    this.ingredientsButton = this.querySelectorAll("details [data-ingredient]");
    this.filterTypes = this.querySelectorAll("[data-filter-type]");
    this.ingredientFilterType = this.querySelector(
      "[data-filter-type]:checked"
    ).value;
    this.ingredientsDetails = this.querySelector("details.pi-filter-details");
    this.clearFiltersButton = this.querySelector("[data-clear-filters]");
    this.activeFiltersWrapper = this.querySelector(
      "[data-active-filters-wrapper]"
    );
    this.healthBenefits = [];
    this.healthBenefitButtons = this.querySelectorAll("[data-health-benefit]");
    this.healthBenefitsDetails = this.querySelector(
      "details#Details-filter-health-benefits"
    );
  }
  connectedCallback() {
    this.collectionButtons.forEach((button) => {
      button.addEventListener("click", this.onCollectionClick.bind(this));
    });
    this.ingredientsButton.forEach((button) => {
      button.addEventListener("click", this.onIngredientClick.bind(this));
    });
    this.filterTypes.forEach((type) => {
      type.addEventListener("change", this.onFilterTypeChange.bind(this));
    });
    this.healthBenefitButtons.forEach((button) => {
      button.addEventListener("click", this.onHealthBenefitClick.bind(this));
    });
    document.addEventListener("click", this._onDocumentClick.bind(this));
    this.init();
// --- START: NEW URL Parameter Logic ---
    const urlParams = new URLSearchParams(window.location.search);

    // Check for Health Benefits
    const healthBenefitsParam = urlParams.get('health_benefits');
    if (healthBenefitsParam) {
        const benefitsToSelect = healthBenefitsParam.split(',');
        benefitsToSelect.forEach(benefitSlug => {
            const button = this.querySelector(`[data-health-benefit="${benefitSlug.trim()}"]`);
            if (button && !button.classList.contains('pi-filter-button-selected')) {
                button.click();
            }
        });
    }

    // Check for Ingredients (Does Contain)
    const ingredientsParam = urlParams.get('ingredients');
    if (ingredientsParam) {
        const containsRadio = this.querySelector('[data-filter-type][value="contains"]');
        if (containsRadio && !containsRadio.checked) {
            containsRadio.click();
        }
        const ingredientsToSelect = ingredientsParam.split(',');
        ingredientsToSelect.forEach(ingredientSlug => {
            const button = this.querySelector(`details [data-ingredient="${ingredientSlug.trim()}"]:not([data-not])`);
            if (button && !button.classList.contains('pi-filter-button-selected')) {
                 const oppositeButton = this.querySelector(`details [data-ingredient="${ingredientSlug.trim()}"][data-not].pi-filter-button-selected`);
                 if (oppositeButton) {
                     oppositeButton.click();
                 }
                button.click();
            }
        });
    }

     // Check for Negative Ingredients (Doesn't Contain)
     const notIngredientsParam = urlParams.get('not_ingredients');
     if (notIngredientsParam) {
         const notContainsRadio = this.querySelector('[data-filter-type][value="not"]');
         if (notContainsRadio && !notContainsRadio.checked) {
             notContainsRadio.click();
         }
         const ingredientsToSelect = notIngredientsParam.split(',');
         ingredientsToSelect.forEach(ingredientSlug => {
             const button = this.querySelector(`details [data-ingredient="${ingredientSlug.trim()}"][data-not]`);
             if (button && !button.classList.contains('pi-filter-button-selected')) {
                  const oppositeButton = this.querySelector(`details [data-ingredient="${ingredientSlug.trim()}"]:not([data-not]).pi-filter-button-selected`);
                  if (oppositeButton) {
                      oppositeButton.click();
                  }
                 button.click();
             }
         });
     }

    // --- Keep the original Collection logic, slightly modified ---
    this.collection = this.url.searchParams.get("collection"); // Use urlParams here too
    if (this.collection) {
      this.collection = this.collection.trim().toLowerCase();
      const collectionButton = this.querySelector(
        `[data-product-type="${this.collection}"]`
      );
      // Only click if it exists and isn't already selected
      if (collectionButton && !collectionButton.classList.contains('pi-filter-button-selected')) {
        // Ensure 'all' is deselected *before* clicking the specific collection
        const allButton = this.querySelector('[data-product-type="all"].pi-filter-button-selected');
        if (allButton) {
             // We need to manually remove the class and update state if clicking 'all' doesn't trigger dispatch correctly initially
             allButton.classList.remove('pi-filter-button-selected');
             this.selectedCollection = this.collection; // Update state directly before click
        }
        collectionButton.click(); // Simulate click
      }
    } else if (!healthBenefitsParam && !ingredientsParam && !notIngredientsParam) {
        // If NO filters are set via URL, ensure 'All' collection is selected by default
        const allButton = this.querySelector('[data-product-type="all"]');
        if (allButton && !allButton.classList.contains('pi-filter-button-selected')) {
            // Check if any other collection button is selected and deselect it
            const otherSelected = this.querySelector('[data-product-type]:not([data-product-type="all"]).pi-filter-button-selected');
            if(otherSelected){
                otherSelected.classList.remove('pi-filter-button-selected');
            }
             allButton.click(); // Click 'All' only if nothing else is selected/specified
        }
    }
    // --- END: NEW/Modified URL Parameter Logic ---
  }

  

  init() {
    this.clearFiltersButton = this.querySelector("[data-clear-filters]");
    if (this.clearFiltersButton) {
      this.clearFiltersButton.addEventListener(
        "click",
        this.onClearFiltersClick.bind(this)
      );
    }
    this.querySelectorAll("[data-filter-remove]").forEach((button) => {
      button.addEventListener("click", this.onFilterRemoveClick.bind(this));
    });
  }

  _onDocumentClick(event) {
    // Check and close Ingredients dropdown
    if (this.ingredientsDetails && this.ingredientsDetails.open && !this.ingredientsDetails.contains(event.target)) {
      this.ingredientsDetails.open = false;
    }
    // ADD THIS BLOCK to check and close Health Benefits dropdown
    if (this.healthBenefitsDetails && this.healthBenefitsDetails.open && !this.healthBenefitsDetails.contains(event.target)) {
      this.healthBenefitsDetails.open = false;
    }
  }

  onCollectionClick(event) {
    event.preventDefault();
    this.selectedCollection = event.target.dataset.productType;
    this.collectionButtons.forEach((button) => {
      if (button.dataset.productType == this.selectedCollection) {
        button.classList.add("pi-filter-button-selected");
        this.url.searchParams.set("collection", this.selectedCollection);
        window.history.replaceState({}, "", this.url);
      } else {
        button.classList.remove("pi-filter-button-selected");
      }
    });
    this._dispatchEvent();
  }

  onIngredientClick(event) {
    event.preventDefault();
    const target = event.target;
    const ingredientValue = event.target.dataset.ingredientName;
    const isNegative = target.hasAttribute("data-not");
    const isActive = target.classList.contains("pi-filter-button-selected");

    this.querySelectorAll(
      `details [data-ingredient='${ingredientValue}']`
    ).forEach((button) => {
      if (button != target) {
        if (isActive) {
          button.removeAttribute("disabled");
        } else {
          button.setAttribute("disabled", "");
        }
      }
    });

    if (isActive) {
      target.classList.remove("pi-filter-button-selected");
      if (isNegative) {
        this.negativeIngredients = this.negativeIngredients.filter(
          (ingredient) => ingredient != ingredientValue
        );
      } else {
        this.ingredients = this.ingredients.filter(
          (ingredient) => ingredient != ingredientValue
        );
      }
    } else {
      target.classList.add("pi-filter-button-selected");
      if (isNegative) {
        this.negativeIngredients.push(ingredientValue);
      } else {
        this.ingredients.push(ingredientValue);
      }
    }
    this._dispatchEvent();
  }

  onFilterTypeChange(event) {
    const value = event.target.value;
    this.ingredientFilterType = value;
    if (this.ingredientFilterType == "not") {
      this.setAttribute("data-negative-filter", "");
    } else {
      this.removeAttribute("data-negative-filter");
    }
  }
  onHealthBenefitClick(event) {
    event.preventDefault();
    const target = event.target;
    const benefitValue = target.dataset.benefitName; // Use data-benefit-name for the string value
    const isActive = target.classList.contains("pi-filter-button-selected");

    if (isActive) {
      target.classList.remove("pi-filter-button-selected");
      this.healthBenefits = this.healthBenefits.filter(
        (benefit) => benefit != benefitValue
      );
    } else {
      target.classList.add("pi-filter-button-selected");
      this.healthBenefits.push(benefitValue);
    }
    this._dispatchEvent();
  }

  onClearFiltersClick(event) {
    event.preventDefault();
    this.selectedCollection = null;
    this.ingredients = [];
    this.negativeIngredients = [];
    this.healthBenefits = [];
    this.collectionButtons.forEach((button) => {
      if (button.dataset.productType == "all") {
        button.classList.add("pi-filter-button-selected");
      } else {
        button.classList.remove("pi-filter-button-selected");
      }
    });
    this.ingredientsButton.forEach((button) => {
      button.classList.remove("pi-filter-button-selected");
      button.removeAttribute("disabled");
    });
    this.healthBenefitButtons.forEach((button) => {
      button.classList.remove("pi-filter-button-selected");
      button.removeAttribute("disabled");
    });

    this._dispatchEvent();
  }

  _dispatchEvent() {
    this.renderActiveFilters();

    var ingredientFilter = [...new Set(this.ingredients)].filter(Boolean);
    var negativeIngredientFilter = [
      ...new Set(this.negativeIngredients),
    ].filter(Boolean);
    var healthBenefitFilter = [...new Set(this.healthBenefits)].filter(Boolean);

    this.bundleBuilder =
      this.bundleBuilder || this.closest("wiq-bundle-builder");

    this.gridItems =
      this.gridItems || this.bundleBuilder.querySelectorAll("wiq-bundle-item");


this.gridItems.forEach((item) => {
      const productId = item.dataset.productId;


      // --- Robust Data Parsing START ---
      let productIngredients = [];
      let tags = [];
      let productHealthBenefits = [];

      try {
          const rawIngredients = item.dataset.productIngredients || "[]";
          const parsedIngredients = JSON.parse(rawIngredients);
          productIngredients = Array.isArray(parsedIngredients) ? parsedIngredients : [];
      } catch (e) {
          console.error(` -> ERROR parsing ingredients for product ${productId}:`, item.dataset.productIngredients, e);
          productIngredients = []; // Default to empty array on error
      }

      try {
          const rawTags = item.dataset.tags || "[]";
          const parsedTags = JSON.parse(rawTags);
          tags = Array.isArray(parsedTags) ? parsedTags : [];
      } catch (e) {
          console.error(` -> ERROR parsing tags for product ${productId}:`, item.dataset.tags, e);
          tags = []; // Default to empty array on error
      }

      try {
          const rawBenefits = item.dataset.healthBenefits || "[]";
          const parsedBenefits = JSON.parse(rawBenefits);
          productHealthBenefits = Array.isArray(parsedBenefits) ? parsedBenefits : [];
      } catch (e) {
          console.error(` -> ERROR parsing health benefits for product ${productId}:`, item.dataset.healthBenefits, e);
          productHealthBenefits = []; // Default to empty array on error
      }
      // --- Robust Data Parsing END ---




      var inCollection = true;
      var ingredientCondition = true;
      var negativeIngredientCondition = false;
      var healthBenefitCondition = true;

      // Collection Check
      if (this.selectedCollection && this.selectedCollection !== "all") {
        // Check if ANY tag matches the selected collection (case-insensitive, trimmed)
        inCollection = tags.some(tag => tag.toLowerCase().trim() === this.selectedCollection.toLowerCase().trim());

      }

      // Ingredient Check (Must contain ALL selected ingredients)
      if (ingredientFilter.length > 0) {
        ingredientCondition = ingredientFilter.every((selectedIngredient) => {
          const lowerSelected = selectedIngredient.toLowerCase().trim();
          const match = productIngredients.some((productIngredient) =>
            productIngredient.toLowerCase().trim() === lowerSelected
          );
          return match;
        });

      }

      // Negative Ingredient Check (Must NOT contain ANY selected negative ingredients)
      if (negativeIngredientFilter.length > 0) {
        negativeIngredientCondition = negativeIngredientFilter.some((selectedIngredient) => {
          const lowerSelected = selectedIngredient.toLowerCase().trim();
          const match = productIngredients.some((productIngredient) =>
              productIngredient.toLowerCase().trim() === lowerSelected
            );

          return match; // If ANY match is found, the negative condition is true
        });

      }

      // Health Benefit Check (Must contain ALL selected benefits)
      if (healthBenefitFilter.length > 0) {
        healthBenefitCondition = healthBenefitFilter.every((selectedBenefit) => {
          const lowerSelected = selectedBenefit.toLowerCase().trim();
          const match = productHealthBenefits.some((productBenefit) =>
            productBenefit.toLowerCase().trim() === lowerSelected
          );

          return match;
        });

      }

      // Final decision
      const shouldShow = inCollection && ingredientCondition && !negativeIngredientCondition && healthBenefitCondition;


      if (shouldShow) {
        item.removeAttribute("hidden");
      } else {
        item.setAttribute("hidden", "");
      }
    }); // End of forEach loop

    if (
      this.bundleBuilder.querySelectorAll("wiq-bundle-item:not([hidden])")
        .length == 0
    ) {
      this.bundleBuilder.setAttribute("data-no-items", "");

    } else {
      this.bundleBuilder.removeAttribute("data-no-items");

    }
  } // End of _dispatchEvent method

  renderActiveFilters() {
    var html = [];
    this.querySelectorAll(
      "[data-health-benefit].pi-filter-button-selected"
    ).forEach((button) => {
html.push(`<button data-filter-remove="${button.dataset.healthBenefit}" data-remove-type="health-benefit" class="pi-active-filter__inner button button--tertiary" type="button">${button.dataset.benefitName} <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path><path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg></button>`);
    });
    this.querySelectorAll(
      "[data-ingredient].pi-filter-button-selected"
    ).forEach((button) => {
      const isNegative = button.hasAttribute("data-not");
      if (isNegative) {
        html.push(`<button data-not data-filter-remove="${button.dataset.ingredient}" class="pi-active-filter__inner button button--tertiary" type="button">
          ${button.dataset.ingredientName}
          <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </button>`);
      } else {
        html.push(`<button  data-filter-remove="${button.dataset.ingredient}" class="pi-active-filter__inner button button--tertiary" type="button">
          ${button.dataset.ingredientName}
          <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
          <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </button>`);
      }
    });
    if (html.length > 0) {
      html.push(
        `<button class="pi-filter-button-clear" data-clear-filters>Remove All</button>`
      );
    }
    this.activeFiltersWrapper.innerHTML = html.join("");
    this.init();
  }

  onFilterRemoveClick(event) {
    event.preventDefault();
    const target = event.target.closest('button[data-filter-remove]');
    if (!target) return;

    const value = target.dataset.filterRemove;
    const type = target.dataset.removeType;
    const isNegative = target.hasAttribute("data-not");
    
    let buttonToClick;

    if (type === 'health-benefit') {
      buttonToClick = this.querySelector(`[data-health-benefit="${value}"]`);
    } else {
      // Original Ingredient logic
      buttonToClick = this.querySelector(`[data-ingredient="${value}"]:not([data-not])`);
      if (isNegative) {
        buttonToClick = this.querySelector(
          `[data-ingredient="${value}"][data-not]`
        );
      }
    }

    if (buttonToClick) {
      buttonToClick.click();
    }
  }
}

customElements.define("wiq-bundle-filters", WIQBundleFilters);

class PiCheckoutModal extends HTMLElement {
  constructor() {
    super();
    this.currentBtn = null;
    this.querySelector("[data-close-modal]").addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    this.addEventListener("click", (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
    this.querySelectorAll("[data-checkout-button]").forEach((button) => {
      button.addEventListener("click", this.onCheckoutButtonClick.bind(this));
    });
    window.addEventListener('pageshow', this.handlePageShow.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow.bind(this));
  }

  show() {
    this.setAttribute("data-modal-active", "");
    document.body.classList.add("overflow-hidden");
  }

  hide() {
    this.removeAttribute("data-modal-active");
    document.body.classList.remove("overflow-hidden");
  }

  handlePageShow(event) {
    if (event.persisted) {
      if (this.hasAttribute('data-modal-active')) {
        this.hide();
      }
      
      this.querySelectorAll('[data-checkout-button]').forEach(btn => {
        btn.removeAttribute('data-loading');
        btn.removeAttribute('disabled');
      });
      
      const bundleBuilder = document.querySelector('wiq-bundle-builder');
      if (bundleBuilder) {
        const checkoutOpener = bundleBuilder.querySelector('[data-checkout-modal-opener]');
        if (checkoutOpener) {
          checkoutOpener.removeAttribute('data-loading');
          checkoutOpener.removeAttribute('disabled');
        }
      }
    }
  }

  onCheckoutButtonClick(event) {
    event.preventDefault();
    this.currentBtn = event.target;
    const type = event.target.dataset.checkoutButton;
    const bundleBuilder = document.querySelector("wiq-bundle-builder");
    
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'Checkout Method Selected',
        'checkoutMethod': type,
        'itemCount': bundleBuilder ? bundleBuilder.totalCount : 0
      });
    }
    
    if (bundleBuilder) {
      if (type == "subscription") {
        bundleBuilder.addBundleToCart("subscription", this);
      } else {
        bundleBuilder.addBundleToCart("one-time", this);
      }
    }
  }
}
customElements.define("pi-checkout-modal", PiCheckoutModal);

class WIQProgressBar extends HTMLElement {
  constructor() {
    super();
    this.loop = null;
    this.bundleDetails = null;
    this.steps = [];
    this.shippingThreshold = null;
    this.maxProgress = null;
    this.stepsContainer = this.querySelector("[data-html]");
    this.freeShippingTemplate = this.querySelector("template");
    this.toolTip = this.querySelector("[data-progress-message]");
    this.tippyInstance = null;
    this.upsellText = "";
    this.initialized = false;
  }
  init(loop) {
    this.loop = loop;
    this.steps = [...this.loop.discountList];
    this.maxProgress = Math.max(
      ...this.steps.map((d) => d.minCartQuantity || 0)
    );
    this.steps.sort((a, b) => a.minCartQuantity - b.minCartQuantity);
    this.renderSteps();
    this.initialized = true;
  }
  renderSteps() {
    var html = [];
    html.push(`
          <div class="pi-cart-progress__step" style="left: 0%;" data-step="0">
            <div class="pi-cart-progress__marker">0</div>
          </div>`);

    this.steps.forEach((step) => {
      var left = (step.minCartQuantity / this.maxProgress) * 100;
      if (step.conditionType == "shipping") {
        html.push(
          TemplateEngine.render(
            this.freeShippingTemplate.innerHTML,
            {
              step: step.minCartQuantity,
              left: left,
            },
            true
          )
        );
      } else {
        html.push(`
          <div class="pi-cart-progress__step" style="left: ${left}%;" data-step="${step.minCartQuantity}">
            <div class="pi-cart-progress__marker">${step.minCartQuantity}</div>
          </div>`);
      }
    });
    this.stepsContainer.innerHTML = html.join("");
  }

  updateProgress(count) {
    if (!this.initialized) return;
    var progress = (count / this.maxProgress) * 100;
    if (progress > 100) {
      progress = 100;
    }
    this.style.setProperty("--pi-cart-progress", `${progress}%`);
    this.querySelectorAll("[data-step]").forEach((step) => {
      if (count >= parseInt(step.dataset.step)) {
        step.setAttribute("data-active", "");
      } else {
        step.removeAttribute("data-active");
      }
    });

    const closestDiscount = this.loop.getClosestDiscount(count, true);
    var left = closestDiscount.minCartQuantity - count;
    var itemText = left > 1 ? "items" : "item";
    var message = `${left} ${itemText} to get ${closestDiscount.value}% discount`;

    if (closestDiscount.conditionType == "shipping" && left == 0) {
      message = `You're eligible for Free Shipping!`;
    } else if (closestDiscount.conditionType == "shipping" && left > 0) {
      message = `Add ${left} ${itemText} to get Free Shipping`;
    } else if (left == 0) {
      message = `You're eligible for a ${closestDiscount.value}% discount!`;
    }

    if (progress == 100) {
      message = "You've reached the maximum discount tier!";
    }

    this.upsellText = message;
    if (this.tippyInstance) {
      this.tippyInstance.setContent(this.upsellText);
      if (count == 0) {
        this.tippyInstance.destroy();
        this.tippyInstance = null;
      }
    } else if (count > 0) {
      this.initTippy();
    }
  }

  initTippy() {
    this.tippyInstance = tippy(this.toolTip, {
      arrow: true,
      content: this.upsellText,
      trigger: "manual",
      placement: "top",
      showOnCreate: true,
      animation: false,
      hideOnClick: false,
      sticky: true,
      offset: [0, 14],
      appendTo: this.parentElement,
      maxWidth: 120,
    });
  }
}

customElements.define("wiq-progress-bar", WIQProgressBar);

class WIQBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.shippingFee = parseFloat(this.dataset.shippingFee);
    this.shippingThreshold = parseInt(this.dataset.shippingThreshold);
    this.minimumLimit = parseInt(this.dataset.minimumLimit);
    this.subscriptionPercentage =
      parseFloat(this.dataset.subscriptionPercentage) / 100;
    this.loop = new LocalBundlePricing(this.shippingThreshold);
    this.countWithGiftCards = 0;
    this.totalCount = 0;
    this.selectedItems = [];
    this.selectedItemsContainer = this.querySelector("[data-selected-items]");
    this.itemsCountEl = this.querySelectorAll("[data-items-count]");
    this.subtotalEl = this.querySelector("[data-subtotal]");
    this.volumeDiscountEl = this.querySelector("[data-volume-discount]");
    this.subscriptionDiscountEl = this.querySelector(
      "[data-subscription-discount]"
    );
    this.shippingEl = this.querySelector("[data-shipping]");
    this.progressBar = this.querySelector("wiq-progress-bar");

    this.purchaseOptions = this.querySelector("wiq-purchase-options");
    this.subscriptionFrequency = this.querySelector("pi-dropdown-menu");

    this.upsellText = this.querySelectorAll("[data-upsell-text]");
    this.viewBundleCart = this.querySelector("[data-view-bundle-cart]");
    this.keepShopping = this.querySelectorAll("[data-keep-shopping]");

    this.checkoutModalOpener = this.querySelector(
      "[data-checkout-modal-opener]"
    );
    this.checkoutTotalPrice = this.querySelector("[data-checkout-total-price]");
    this.checkoutButtonText = this.querySelector("[data-checkout-button-text]");

    this.onBundleChange = this.onBundleChange.bind(this);
    this.onPurchaseOptionsChange = this.onPurchaseOptionsChange.bind(this);
    // Modal Checkout Button
    this.checkoutButton = null;

    this.subscriptionConsent = this.querySelector(
      "[data-subscription-consent]"
    );

    this.storage = new WIQStorage("wiq-bundle-builder");
  }

  trackBundlePageView() {
    // Calculate current bundle value
    const bundleValue = this.totalPrice ? (this.totalPrice / 100).toFixed(2) : '0.00';
    
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'Viewed Custom Bundle',
        'ecommerce': {
          'detail': {
            'products': [{
              'id': this.dataset.bundleId || 'custom-bundle',
              'name': 'Custom Bundle',
              'price': bundleValue,
              'category': 'Bundle',
              'variant': 'custom'
            }]
          }
        }
      });
    }
  }

  trackItemAddedToBundle(item, quantity, isNewItem, previousQuantity = 0) {
    if (!item) return;

    const productPrice = (parseFloat(item.dataset.price) / 100).toFixed(2);
    const productTitle = item.querySelector('.product-card__title')?.textContent || 'Product';

    // Units actually gained by this interaction. Adding a brand new item is a
    // delta from 0; bumping 2 -> 5 with the plus button is a delta of 3. Only a
    // positive delta is an "add" - quantity decreases arrive here too (any
    // quantity > 0 routes through this branch) and must not fire AddToCart.
    const addedQuantity = quantity - previousQuantity;

    if (window.dataLayer) {
      window.dataLayer.push({
        'event': isNewItem ? 'Added to Bundle' : 'Bundle Quantity Updated',
        'ecommerce': {
          'add': {
            'products': [{
              'id': item.dataset.productId,
              'name': productTitle,
              'price': productPrice,
              'quantity': quantity,
              'variant': item.dataset.variantId || ''
            }]
          }
        }
      });
    }

    // Meta's AddToCart is deliberately NOT sent from here any more.
    //
    // Now that the cart is mirrored into Shopify as items are added, Shopify's
    // own product_added_to_cart fires and the Facebook & Instagram channel's web
    // pixel sends AddToCart itself. Verified in the live request:
    //   a   = shopify_web_pixel
    //   eid = sh-b0762776-...          (dedup key against the CAPI event)
    //   cd[content_ids] = [42181129437273]   (variant id)
    //   cd[content_category] = Seasonal Smoothie
    //
    // Shopify's event is strictly better than the hand-rolled one it replaces:
    //   - it carries eid, so Meta merges the browser and server copies instead
    //     of counting them twice (our call set no eid and could never dedup)
    //   - it sends variant ids, which is what Shopify's Meta catalog feed keys
    //     on; we were sending product ids, so our events almost certainly never
    //     matched the catalog for dynamic product ads
    //   - it is accompanied by a Conversions API copy, which is the whole point
    //     of the sync: Events Manager showed ~4,200 browser vs ~10 server
    //     AddToCart events for July 2026, and no Event Match Quality score
    //
    // Keeping both would double-count AddToCart and pollute catalog matching
    // with unmatchable ids.

    // GA4 / Google Ads. The dataLayer push above reaches nothing on its own -
    // this store loads a Google tag (GT-TB64QVB), not a Tag Manager container,
    // and Google tags don't translate arbitrary dataLayer custom events into
    // GA4 events. Call gtag directly using GA4's standard add_to_cart name and
    // items schema so GA4 (G-4ER75NW3B5) and Google Ads (AW-925966515) record it.
    if (typeof gtag === 'function' && addedQuantity > 0) {
      gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: parseFloat(productPrice) * addedQuantity,
        items: [{
          item_id: item.dataset.productId,
          item_name: productTitle,
          price: parseFloat(productPrice),
          quantity: addedQuantity
        }]
      });
    }

    // Klaviyo. Deliberately named "Added to Bundle" rather than "Added to Cart".
    // Two "Added to Cart" metrics already exist - ShHNbX (API, dead all year) and
    // WExAm2 (Shopify, which collapsed from 827/mo in May to 3 in June when the
    // native cart events stopped firing). Reusing that name would revive the dead
    // one and leave two live metrics with the same name but different semantics
    // once WExAm2 recovers. A distinct name keeps bundle-builder activity
    // unambiguous and is honest about what it measures: an add to the custom
    // bundle, which is not yet a Shopify cart line.
    if (window._learnq && typeof window._learnq.push === 'function' && addedQuantity > 0) {
      window._learnq.push(['track', 'Added to Bundle', {
        $value: parseFloat(productPrice) * addedQuantity,
        ProductName: productTitle,
        ProductID: item.dataset.productId,
        VariantID: item.dataset.variantId || '',
        Quantity: addedQuantity,
        TotalQuantityInBundle: quantity,
        Price: parseFloat(productPrice),
        URL: item.dataset.productHandle
          ? `${window.location.origin}/products/${item.dataset.productHandle}`
          : window.location.href,
        // this.totalCount is not recalculated until after this call returns, so
        // it still holds the pre-change total here. Gift cards are excluded from
        // totalCount, so only a non-gift-card add moves it.
        BundleItemCount: item.hasAttribute('data-gift-card')
          ? this.totalCount
          : this.totalCount + addedQuantity,
        PurchaseOption: this.purchaseOptions
          ? this.purchaseOptions.selectedOption
          : null
      }]);
    }
  }

  trackItemRemovedFromBundle(item, removedQuantity = 1) {
    if (!item) return;

    const productPrice = (parseFloat(item.dataset.price) / 100).toFixed(2);
    const productTitle = item.querySelector('.product-card__title')?.textContent || 'Product';

    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'Removed from Bundle',
        'ecommerce': {
          'remove': {
            'products': [{
              'id': item.dataset.productId,
              'name': productTitle,
              'price': productPrice
            }]
          }
        }
      });
    }

    // GA4 counterpart to the add_to_cart call in trackItemAddedToBundle.
    if (typeof gtag === 'function') {
      gtag('event', 'remove_from_cart', {
        currency: 'USD',
        value: parseFloat(productPrice) * removedQuantity,
        items: [{
          item_id: item.dataset.productId,
          item_name: productTitle,
          price: parseFloat(productPrice),
          quantity: removedQuantity
        }]
      });
    }
  }

connectedCallback() {
    this.checkForCheckoutCompletion();
    
    // Instantly load the local Appstle pricing tiers without calling any external APIs
    this.progressBar.init(this.loop);

    this.modalQueue = [];

    if (this.hasActiveOrPausedSubscription()) {
      this.queueSubscriptionNoticeModal();
    }

    const wasPrepopulatedFromUrl = this.loadFromUrlParams();
    this.applyDiscountFromUrlParams();
    if (!wasPrepopulatedFromUrl) {
      this.loadLocalCart();
    }

    this.renderPrice();
    this.progressBar.updateProgress(this.totalCount);
    this.trackBundlePageView();

    document.addEventListener("wiq:bundle:change", this.onBundleChange);
    this.purchaseOptions.addEventListener(
      "wiq:purchase-options:change",
      this.onPurchaseOptionsChange
    );
    document.addEventListener(
      "wiq:bundle:clear",
      this.onBundleClear.bind(this)
    );
    this.viewBundleCart.addEventListener(
      "click",
      this.onViewBundleCartClick.bind(this)
    );

    this.keepShopping.forEach((el) => {
      el.addEventListener("click", () => {
        this.parentElement.removeAttribute("data-mobile-cart-drawer");
      });
    });
    this.checkoutModalOpener.addEventListener(
      "click",
      this.handleCheckoutModalOpener.bind(this)
    );

    this.querySelectorAll('[data-clear-entire-cart]').forEach((btn) => {
      btn.addEventListener('click', async (event) => {
        event.preventDefault();
        if (btn.hasAttribute('data-clearing')) return;
        btn.setAttribute('data-clearing', '');
        const original = btn.textContent;
        btn.textContent = 'Clearing…';
        try {
          await this.clearEntireCart();
        } finally {
          btn.removeAttribute('data-clearing');
          btn.textContent = original;
        }
      });
    });

    // Removed a second wiq:bundle:change listener here that re-fired
    // trackBundlePageView() 100ms after every add, remove and quantity click.
    // "Viewed Custom Bundle" is a page-view event and is already dispatched once
    // above; re-firing it dozens of times per session inflated view counts and
    // buried the genuine add/remove events in the dataLayer. Item-level activity
    // is covered by trackItemAddedToBundle / trackItemRemovedFromBundle.

    this.processModalQueue();
  }

  checkForCheckoutCompletion() {
    const checkoutAttempted = sessionStorage.getItem('bundle_checkout_attempted');
    if (!checkoutAttempted) return;

    // We've just come back from a checkout hand-off. connectedCallback() calls
    // loadLocalCart() synchronously right after this, so an async /cart.js check
    // loses the race - it repaints the just-ordered box from localStorage before
    // the check resolves, which is why the cart looked "stuck" after placing an
    // order. Clear the local cart NOW, synchronously, and keep a snapshot to put
    // back only if the checkout turns out to have been abandoned (Shopify cart
    // still has items) or we can't confirm it emptied.
    const snapshot = this.storage.get() || [];
    this.storage.clear();

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => cart && cart.item_count === 0)
      .catch(() => false)
      .then((confirmedEmpty) => {
        sessionStorage.removeItem('bundle_checkout_attempted');
        if (!confirmedEmpty && snapshot.length) {
          // Order not placed (or unverifiable) - restore the local box.
          this.storage.set(snapshot);
          this.loadLocalCart();
          this.renderPrice();
          this.progressBar.updateProgress(this.totalCount);
        }
      });
  }

  // dd
  // handleCheckoutModalOpener() {
  //   const modal = document.querySelector("pi-checkout-modal");

  //   if (this.purchaseOptions.selectedOption == "subscription") {
  //     const sellingPlan = this.subscriptionFrequency.selectedValue;
  //     if (!sellingPlan || !this.subscriptionConsent.checked) {
  //       this.subscriptionFrequency._focus();
  //       return;
  //     }
  //   }
  //   if (modal) {
  //     modal.setAttribute(
  //       "data-checkout-type",
  //       this.purchaseOptions.selectedOption
  //     );
  //     modal.show();
  //   }
  // }

  loadLocalCart() {
    const localItems = this.storage.get();
    if (localItems) {
      localItems.reverse().forEach((item) => {
        this.onBundleChange({
          detail: {
            productId: item.productId,
            quantity: item.quantity,
            variantId: item.variantId || null,
            properties: item.properties || null,
            fromLocalCart: true,
          },
        });
      });
    }
  }

    loadFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("bundle_items");
    if (!raw) return false;

    const items = raw
      .split(",")
      .map((pair) => {
        const [productId, quantityStr] = pair.split(":");
        return {
          productId: (productId || "").trim(),
          quantity: parseInt(quantityStr, 10),
        };
      })
      .filter(
        (entry) =>
          entry.productId &&
          entry.quantity > 0 &&
          this.querySelector(
            `wiq-bundle-item[data-product-id="${entry.productId}"]`
          )
      );

    // Strip the param so refresh/back-button doesn't replace the cart again
    params.delete("bundle_items");
    const query = params.toString();
    const newUrl =
      window.location.pathname +
      (query ? `?${query}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);

    if (items.length === 0) return false;

    items.forEach((entry) => {
      this.onBundleChange({
        detail: {
          productId: entry.productId,
          quantity: entry.quantity,
          fromPredefinedBundle: true,
        },
      });
    });

    this.showPrepopulatedModal();
    return true;
  }

  // Shopify's /discount/CODE?redirect=X mechanism strips any query string
  // from X when it redirects - documented Shopify limitation, incompatible
  // with also needing bundle_items in the same link. So links combining a
  // discount with a prepopulated bundle go straight to this page with both
  // params in one query string (?bundle_items=...&discount=...), and this
  // applies the discount via a background request instead of a redirect -
  // Shopify sets the discount session cookie on any GET to /discount/CODE,
  // background fetch included, no navigation required.
  applyDiscountFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("discount");
    if (!code) return;

    fetch(`/discount/${encodeURIComponent(code)}`, { credentials: "same-origin" });

    params.delete("discount");
    const query = params.toString();
    const newUrl =
      window.location.pathname +
      (query ? `?${query}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }

  enqueueModal(showFn) {
    this.modalQueue = this.modalQueue || [];
    this.modalQueue.push(showFn);
  }

  processModalQueue() {
    if (!this.modalQueue || this.modalQueue.length === 0) return;
    const showNext = this.modalQueue.shift();
    showNext(() => this.processModalQueue());
  }

  showModal(selector, closeSelector, onClose) {
    const modal = document.querySelector(selector);
    if (!modal) {
      if (onClose) onClose();
      return;
    }

    modal.setAttribute("data-active", "");
    document.body.classList.add("overflow-hidden");

    const closeModal = () => {
      modal.removeAttribute("data-active");
      document.body.classList.remove("overflow-hidden");
      if (onClose) onClose();
    };

    modal.querySelectorAll(closeSelector).forEach((el) => {
      el.addEventListener("click", closeModal, { once: true });
    });
  }

  showPrepopulatedModal() {
    this.enqueueModal((onClose) =>
      this.showModal(
        "[data-prepopulated-modal]",
        "[data-prepopulated-modal-close]",
        onClose
      )
    );
  }

  hasActiveOrPausedSubscription() {
    return this.dataset.hasSubscription === "true";
  }

  queueSubscriptionNoticeModal() {
    if (sessionStorage.getItem("subscription_notice_shown")) return;
    sessionStorage.setItem("subscription_notice_shown", "true");

    this.enqueueModal((onClose) =>
      this.showModal(
        "[data-subscription-notice-modal]",
        "[data-subscription-notice-modal-close]",
        onClose
      )
    );
  }

  updateCheckoutModal(modal) {
    const titleEl = modal.querySelector('[data-modal-title]');
    const subtitleEl = modal.querySelector('[data-modal-subtitle]');
    const subButton = modal.querySelector('[data-checkout-button="subscription"]');
    const oneButton = modal.querySelector('[data-checkout-button="one-time"]');
    const subButtonText = modal.querySelector('[data-checkout-button="subscription"] .pi-checkout-button__text');
    const oneButtonText = modal.querySelector('[data-checkout-button="one-time"] .pi-checkout-button__text');
    
    // Get current discount tier
    const volumeDiscount = this.loop.getCurrentDiscount(this.totalCount);
    const volumeDiscountPercent = volumeDiscount ? volumeDiscount.value : 0;
    const subscriptionDiscountPercent = this.subscriptionPercentage * 100; // Convert to percentage
    
    // Calculate combined discount (multiplicative)
    const combinedDiscountPercent = subscriptionDiscountPercent + volumeDiscountPercent - 
                                    (subscriptionDiscountPercent * volumeDiscountPercent / 100);
    
    const purchaseType = this.purchaseOptions.selectedOption;
    
    // Remove any existing subtitle styling
    if (subtitleEl) {
      subtitleEl.style.display = 'none';
      subtitleEl.textContent = '';
    }
    
    if (purchaseType === 'one-time') {
      // ONE-TIME PURCHASE SCENARIOS
      if (this.totalCount < 24) {
        if (titleEl) titleEl.textContent = 'Last Chance to Save 15%';
        
        // Button text
        if (subButtonText) subButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Checkout with Subscription</span>';
        if (oneButtonText) oneButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Continue with One-Time Purchase</span>';
        
        // Button styling - subscription (green/secondary), one-time (orange/primary)
        subButton.style.backgroundColor = '#1e3e3d';
        oneButton.style.backgroundColor = '#f05b4b';
        
      } else if (this.totalCount >= 24 && this.totalCount < 36) {
        if (titleEl) titleEl.textContent = 'Last Chance to Save an Additional 15%';
        
        if (subButtonText) subButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Checkout with Subscription</span>';
        if (oneButtonText) oneButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Continue with One-Time Purchase</span>';
        
        subButton.style.backgroundColor = '#1e3e3d';
        oneButton.style.backgroundColor = '#f05b4b';
        
      } else { // 36+ items
        if (titleEl) titleEl.textContent = 'Last Chance to Save an Additional 15%';
        
        if (subButtonText) subButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Checkout with Subscription</span>';
        if (oneButtonText) oneButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Continue with One-Time Purchase</span>';
        
        subButton.style.backgroundColor = '#1e3e3d';
        oneButton.style.backgroundColor = '#f05b4b';
      }
      
      // Reorder buttons - subscription on top for one-time purchase
      if (subButton.parentNode && oneButton.nextSibling !== subButton) {
        subButton.parentNode.insertBefore(subButton, oneButton);
      }
      
    } else { // SUBSCRIPTION PURCHASE SCENARIOS
      const cheekyLines = [
        "(because we like you more than your money)",
        "(your wallet will thank you later)",
        "(smart shoppers always save more)",
        "(treat yourself, you deserve it)",
        "(future you says thanks)"
      ];
      const randomCheeky = cheekyLines[Math.floor(Math.random() * cheekyLines.length)];
      
      if (this.totalCount < 24) {
        if (titleEl) titleEl.textContent = "You've Saved 15%";
        
        if (oneButtonText) oneButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Checkout with One-Time Purchase</span>';
        if (subButtonText) subButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Continue with Subscription</span>';
        
      } else if (this.totalCount >= 24 && this.totalCount < 36) {
        if (titleEl) titleEl.textContent = "You've Saved 15% + 5%";
        
        if (oneButtonText) oneButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Checkout with One-Time Purchase</span>';
        if (subButtonText) subButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Continue with Subscription</span>';
        
      } else { // 36+ items
        if (titleEl) titleEl.textContent = "You've Saved 15% + 10%";
        
        if (oneButtonText) oneButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Checkout with One-Time Purchase</span>';
        if (subButtonText) subButtonText.innerHTML = '<span class="pi-checkout-button__text-text">Continue with Subscription</span>';
      }
      
      // Show cheeky subtitle for subscription
      if (subtitleEl) {
        subtitleEl.textContent = randomCheeky;
        subtitleEl.style.display = 'block';
      }
      
      // Button styling - one-time (green/secondary), subscription (orange/primary)
      oneButton.style.backgroundColor = '#1e3e3d';
      subButton.style.backgroundColor = '#f05b4b';
      
      // Reorder buttons - one-time on top for subscription purchase
      if (oneButton.parentNode && subButton.nextSibling !== oneButton) {
        oneButton.parentNode.insertBefore(oneButton, subButton);
      }
    }
    
    // Update the modal content visibility classes
    const subContent = modal.querySelector('.pi-checkout-modal__content--subscription');
    const oneContent = modal.querySelector('.pi-checkout-modal__content--one-time');
    
    if (subContent) subContent.style.display = 'none';
    if (oneContent) oneContent.style.display = 'none';
    
    // Show the unified content box
    const mainContent = modal.querySelector('.pi-checkout-modal__content');
    if (mainContent) mainContent.style.display = 'block';
  }

handleCheckoutModalOpener() {
    // Gate the seasonal-flavor upsell popup in front of the real checkout.
    // The popup is only appropriate once checkout would actually proceed, so
    // mirror runCheckout()'s own early exits first: gift-card-only carts, an
    // unmet item minimum, and (for subscriptions) a missing plan or consent all
    // skip the popup and fall straight through to runCheckout(), which handles
    // them exactly as before. Anything else: offer the upsell if there are
    // in-stock seasonal flavors not already in the box, otherwise check out.
    const giftCardsOnly = this.selectedItems.length > 0 &&
                          this.selectedItems.every((item) => item.isGiftCard);
    const minimumUnmet = this.totalCount < this.minimumLimit && this.minimumLimit > 0;
    let subscriptionIncomplete = false;
    if (this.purchaseOptions.selectedOption == "subscription") {
      subscriptionIncomplete = !this.subscriptionFrequency.selectedValue ||
                               !this.subscriptionConsent.checked;
    }

    if (!giftCardsOnly && !minimumUnmet && !subscriptionIncomplete &&
        window.SeasonalUpsell && window.SeasonalUpsell.shouldIntercept(this)) {
      window.SeasonalUpsell.open(this); // calls this.runCheckout() when resolved
      return;
    }

    this.runCheckout();
}

runCheckout() {


    // Check if cart contains ONLY gift cards
    const hasOnlyGiftCards = this.selectedItems.length > 0 &&
                             this.selectedItems.every(item => item.isGiftCard);
    
    // DIRECT CHECKOUT FOR GIFT-CARD-ONLY CARTS
    if (hasOnlyGiftCards) {

      this.directGiftCardCheckout();
      return;
    }
    
    // Regular bundle logic continues...
    if (this.totalCount < this.minimumLimit && this.minimumLimit > 0) {

      return;
    }

    // --- CORRECTED VALIDATION ---
    if (this.purchaseOptions.selectedOption == "subscription") {
      // Grab the plan directly from your custom dropdown's internal state
      const sellingPlan = this.subscriptionFrequency.selectedValue;
      
      // Enforce that a plan is selected AND the terms checkbox is checked
      if (!sellingPlan || !this.subscriptionConsent.checked) {

        this.subscriptionFrequency._focus();
        return; 
      }
    }

    // Fire Checkout Tracking Code
    if (window.dataLayer) {
      const products = this.selectedItems.map(item => ({
        'id': item.productId,
        'name': item.handle,
        'price': (item.price / 100).toFixed(2),
        'quantity': item.quantity,
        'variant': item.variantId
      }));
      
      window.dataLayer.push({
        'event': 'Begin Checkout',
        'ecommerce': {
          'checkout': {
            'actionField': {'step': 1},
            'products': products
          }
        }
      });
    }

    // 🔥 MODAL BYPASS: Route directly to checkout processing
    const type = this.purchaseOptions.selectedOption;

    const mockModal = {
      currentBtn: this.checkoutModalOpener,
      hide: () => {},
      querySelector: (selector) => {
        return document.querySelector(selector);
      }
    };

    // One-time, no gift cards: the live-synced cart already holds exactly the
    // right lines, and the native automatic discounts ("5% 24 Item Volume
    // Discount" / "10% 36 Item Volume Discount") apply without any Appstle
    // properties. Reconcile once, then hand off to Shopify directly instead of
    // clearing and rebuilding - a rebuild would re-fire product_added_to_cart
    // for every line and roughly double AddToCart volume. Falls through to the
    // existing rebuild if the reconcile cannot confirm the cart.
    if (this.canCheckoutFromSyncedCart()) {
      this.checkoutDirectFromSyncedCart(type, mockModal);
      return;
    }

    // Execute the checkout immediately
    this.addBundleToCart(type, mockModal);
}

async checkoutDirectFromSyncedCart(type, mockModal) {
  this.checkoutModalOpener.setAttribute("data-loading", "");
  this.checkoutModalOpener.setAttribute("disabled", "");

  try {
    const reconciled = await this.reconcileCartForCheckout();
    if (!reconciled) throw new Error('cart reconcile failed');

    // Confirm the cart genuinely matches before handing the customer off. Being
    // wrong here means charging for the wrong box, so this verifies rather than
    // assumes, and falls back to the proven rebuild on any mismatch.
    const prefix = this.cartRoutePrefix;
    const cart = await fetch(`${prefix}cart.js`, {
      headers: { Accept: 'application/json' }
    }).then((r) => r.json());

    const desired = this.buildDesiredCartMap();
    const actual = new Map();
    (cart.items || []).forEach((line) => {
      actual.set(line.variant_id, (actual.get(line.variant_id) || 0) + line.quantity);
    });

    let matches = desired.size === actual.size;
    if (matches) {
      for (const [variantId, quantity] of desired) {
        if (actual.get(variantId) !== quantity) { matches = false; break; }
      }
    }

    // Belt and braces for the case above: this path is one-time only, so a line
    // still carrying a selling plan means the cart is not what we think it is.
    // Never hand that to Shopify - fall back to the rebuild, which clears first.
    if (matches && (cart.items || []).some((l) => l.selling_plan_allocation || l.selling_plan)) {
      console.warn('Synced cart still holds a selling plan; using rebuild path.');
      matches = false;
    }

    if (!matches) {
      console.warn('Synced cart did not match bundle; using rebuild path.');
      this.checkoutModalOpener.removeAttribute("data-loading");
      this.checkoutModalOpener.removeAttribute("disabled");
      this.addBundleToCart(type, mockModal);
      return;
    }

    // Order note still needs applying - the rebuild path normally does this.
    // Always write it, even when empty: the rebuild path used to clear the cart
    // (which drops the note), so without this an old note from an abandoned
    // session would silently ride along on a new order.
    const orderNoteInput = document.querySelector('[data-gift-note-input]');
    const orderNote = orderNoteInput ? orderNoteInput.value.trim() : '';
    const upsellAttributes = this.seasonalUpsellCartAttributes();
    if (orderNote || (cart.note && cart.note.length > 0) || upsellAttributes) {
      const updateBody = { note: orderNote };
      if (upsellAttributes) updateBody.attributes = upsellAttributes;
      await fetch(`${prefix}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateBody)
      });
    }

    sessionStorage.setItem('bundle_checkout_attempted', 'true');
    sessionStorage.setItem('bypass_cart_redirect', 'true');
    sessionStorage.removeItem('bundle_gift_notes');

    window.location.href = `${prefix}checkout`;
  } catch (error) {
    console.error('Direct checkout failed, falling back to rebuild:', error);
    this.checkoutModalOpener.removeAttribute("data-loading");
    this.checkoutModalOpener.removeAttribute("disabled");
    this.addBundleToCart(type, mockModal);
  }
}
async directGiftCardCheckout() {
    // Same reasoning as addBundleToCart: a pending sync must not re-add lines
    // between the clear and the form submit.
    clearTimeout(this._cartSyncTimer);
    this._cartSyncQueued = false;

    // Show loading state on the checkout button
    this.checkoutModalOpener.setAttribute("data-loading", "");
    this.checkoutModalOpener.setAttribute("disabled", "");
    
    try {
      // Prepare gift card items for cart
      const giftCardItems = this.selectedItems.map((item) => {
        return {
          id: parseInt(item.variantId),
          quantity: parseInt(item.quantity),
          selling_plan: null,
          properties: item.properties || null,
        };
      });

      // Clear cart first
      await fetch(`${window.routes.cart_clear_url}.js`, { method: 'POST' });

      sessionStorage.setItem('bundle_checkout_attempted', 'true');
      sessionStorage.setItem('bypass_cart_redirect', 'true');

      const gcForm = document.createElement('form');
      gcForm.method = 'POST';
      gcForm.action = window.routes.cart_add_url;
      gcForm.style.display = 'none';

      const addGcField = (name, value) => {
        if (value === null || value === undefined) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        gcForm.appendChild(input);
      };

      addGcField('return_to', '/checkout');

      giftCardItems.forEach((item, index) => {
        addGcField(`items[${index}][id]`, item.id);
        addGcField(`items[${index}][quantity]`, item.quantity);
        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            addGcField(`items[${index}][properties][${key}]`, value);
          });
        }
      });

      document.body.appendChild(gcForm);
      gcForm.submit();


    } catch (error) {
      console.error('Error during gift card checkout:', error);
      this.checkoutModalOpener.removeAttribute("data-loading");
      this.checkoutModalOpener.removeAttribute("disabled");
    }
  }



  // End Helper Functions

onBundleChange(event) {
  var {
    productId,
    quantity,
    variantId,
    properties,
    fromPredefinedBundle,
    fromLocalCart,
  } = event.detail;
  
  const item = this.querySelector(
    `wiq-bundle-item[data-product-id="${productId}"]`
  );
  
  if (item.hasAttribute("data-gift-card")) {
    if (properties) {
      item.properties = properties;
    }
    if (variantId) {
      item.variantId = variantId;
      item.dataset.variantId = variantId;
    }
    properties = item.properties;
    variantId = item.variantId;
  }

  // Check if item was already in bundle
  const wasInBundle = this.selectedItems.some(i => i.productId == productId);

  // Units on hand before this interaction, captured before selectedItems is
  // rewritten below. Used to report an accurate quantity on removal.
  const priorEntry = this.selectedItems.find(i => i.productId == productId);
  const priorQuantity = priorEntry ? priorEntry.quantity : 0;

  // Update the quantity input in the grid item to reflect the new quantity
  const quantityInput = item.querySelector("wiq-quantity-input input");
  if (quantityInput && quantityInput.value != quantity) {
    quantityInput.value = quantity;
    // Trigger the validation to update button states
    const quantityComponent = item.querySelector("wiq-quantity-input");
    if (quantityComponent && quantityComponent.validateQtyRules) {
      quantityComponent.validateQtyRules();
    }
  }

  if (quantity == 0) {
    // FIXED: For gift cards, match on productId, variantId AND properties
    if (item.hasAttribute("data-gift-card")) {
      this.selectedItems = this.selectedItems.filter(
        (selectedItem) => !(
          selectedItem.productId == productId && 
          selectedItem.variantId == variantId &&
          JSON.stringify(selectedItem.properties || {}) === JSON.stringify(properties || {})
        )
      );
    } else {
      // Regular products - just match on productId
      this.selectedItems = this.selectedItems.filter(
        (selectedItem) => selectedItem.productId != productId
      );
    }
    
    // Check if ANY items of this productId remain
    const anyRemaining = this.selectedItems.some(i => i.productId == productId);
    if (!anyRemaining) {
      item.removeAttribute("data-active");
    }
    
    if (!fromLocalCart && wasInBundle) {
      this.trackItemRemovedFromBundle(item, priorQuantity);
    }
  } else {
    item.setAttribute("data-active", "");
    
    // FIXED: For gift cards, find exact match including variant and properties
    let existingItemIndex = -1;
    
    if (item.hasAttribute("data-gift-card")) {
      existingItemIndex = this.selectedItems.findIndex(
        (existingItem) => 
          existingItem.productId == productId &&
          existingItem.variantId == (variantId || item.dataset.variantId) &&
          JSON.stringify(existingItem.properties || {}) === JSON.stringify(properties || {})
      );
    } else {
      // Regular products - just match on productId
      existingItemIndex = this.selectedItems.findIndex(
        (existingItem) => existingItem.productId == productId
      );
    }

    const isNewItem = existingItemIndex === -1;

    // Read before the entry is overwritten below - trackItemAddedToBundle needs
    // it to work out how many units this interaction actually added.
    const previousQuantity = isNewItem
      ? 0
      : this.selectedItems[existingItemIndex].quantity;

    if (existingItemIndex !== -1) {
      // Update existing item
      this.selectedItems[existingItemIndex] = {
        productId,
        quantity,
        item,
        variantId: variantId || item.dataset.variantId,
        properties: properties || null,
        price: item.getPrice(variantId || item.dataset.variantId),
        handle: item.dataset.productHandle,
        isGiftCard: item.hasAttribute("data-gift-card"),
      };
    } else {
      // Add new item
      this.selectedItems.unshift({
        productId,
        quantity,
        item,
        variantId: variantId || item.dataset.variantId,
        properties: properties || null,
        price: item.getPrice(variantId || item.dataset.variantId),
        handle: item.dataset.productHandle,
        isGiftCard: item.hasAttribute("data-gift-card"),
      });
    }

    if (!fromLocalCart) {
      this.trackItemAddedToBundle(item, quantity, isNewItem, previousQuantity);
    }
  }

  // Recalculate totals
  this.totalCount = 0;
  this.countWithGiftCards = 0;
  this.selectedItems.forEach((item) => {
    if (!item.isGiftCard) {
      this.totalCount += item.quantity;
    }
    this.countWithGiftCards += item.quantity;
  });

  // Update checkout button based on minimum limit
  this.updateCheckoutButton();

  this.renderPrice();
  this.progressBar.updateProgress(this.totalCount);
  this.updateUpsellText();
  this.render();

  // Only auto-open mobile cart for predefined bundles
  if (fromPredefinedBundle) {
    const isMobile = window.innerWidth <= 749;
    if (isMobile) {
      setTimeout(() => {
        this.parentElement.setAttribute("data-mobile-cart-drawer", "");
      }, 200);
    }
  }

  const localItems = this.selectedItems.map((item) => {
    return {
      productId: item.productId,
      variantId: item.variantId,
      quantity: item.quantity,
      properties: item.properties || null,
    };
  });
  
  if (!fromLocalCart) {
    this.storage.set(localItems);
    // Mirror this change into the real Shopify cart so Shopify emits its native
    // product_added_to_cart. That event is what feeds the Conversions API, the
    // app pixels (Meta channel, Google, Klaviyo, Triple Whale), Shopify
    // Analytics and cart abandonment - none of which can see the in-page bundle.
    // Skipped for fromLocalCart because restoring localStorage on page load is
    // not a new add: Shopify's own cart cookie already persists those lines, and
    // re-adding them would inflate AddToCart on every return visit.
    this.requestCartSync();
  }


}

/* ------------------------------------------------------------------
 *  Shopify cart sync
 *
 *  The bundle builder keeps its own cart in selectedItems + localStorage and,
 *  before this existed, only touched the real Shopify cart once - a clear and
 *  full rebuild at checkout. Shopify therefore never fired product_added_to_cart
 *  while a customer built their box, so Meta's Conversions API received almost
 *  no server-side AddToCart (verified in Events Manager: ~4,200 browser events
 *  vs ~10 server events for July 2026, and no Event Match Quality score at all).
 *
 *  These methods reconcile the real cart to selectedItems, debounced so a burst
 *  of plus-button clicks collapses into one pass. Deliberately syncs variant id
 *  and quantity only - no selling_plan, no Appstle properties. Those still get
 *  attached by addBundleToCart's existing form POST at checkout, which is left
 *  untouched because it is what resolved the Cloudflare 403 on AJAX cart adds.
 * ------------------------------------------------------------------ */

get cartRoutePrefix() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

requestCartSync() {
  clearTimeout(this._cartSyncTimer);
  this._cartSyncTimer = setTimeout(() => {
    // Keep a handle on the running sync so reconcileCartForCheckout can wait
    // for it rather than starting a second one alongside it.
    this._cartSyncActive = this.syncShopifyCart();
  }, 800);
}

// variantId -> total quantity the real cart should hold.
buildDesiredCartMap() {
  const desired = new Map();
  this.selectedItems.forEach((item) => {
    const variantId = parseInt(
      item.variantId ||
        (item.item && item.item.dataset ? item.item.dataset.variantId : null),
      10
    );
    if (!variantId || !item.quantity || item.quantity < 1) return;
    desired.set(variantId, (desired.get(variantId) || 0) + item.quantity);
  });
  return desired;
}

async syncShopifyCart() {
  // Collapse overlapping runs. A sync already in progress will pick up any
  // newer state on its trailing re-run rather than racing against this one.
  if (this._cartSyncInFlight) {
    this._cartSyncQueued = true;
    return;
  }
  this._cartSyncInFlight = true;

  try {
    const prefix = this.cartRoutePrefix;
    const cart = await fetch(`${prefix}cart.js`, {
      headers: { Accept: 'application/json' }
    }).then((r) => r.json());

    const desired = this.buildDesiredCartMap();
    const current = new Map();      // variantId -> quantity, plan-less lines only
    const lineKeyByVariant = new Map();
    const decreases = [];
    const increases = [];

    // Lines carrying a selling plan are left over from an abandoned subscription
    // checkout (addBundleToCart's rebuild attaches selling_plan). They must not
    // count toward `current`, because adding a plan-less line for the same
    // variant creates a SECOND line rather than merging - the totals would look
    // correct while the cart actually held a subscription line. A one-time
    // checkout could then hand that to Shopify and charge a subscription.
    // Removing them keeps the browsing cart deterministically plan-less; the
    // subscription path rebuilds from scratch at checkout anyway.
    (cart.items || []).forEach((line) => {
      const hasPlan = !!(line.selling_plan_allocation || line.selling_plan);
      if (hasPlan) {
        decreases.push({ id: line.key, quantity: 0 });
        return;
      }
      current.set(line.variant_id, (current.get(line.variant_id) || 0) + line.quantity);
      if (!lineKeyByVariant.has(line.variant_id)) {
        lineKeyByVariant.set(line.variant_id, line.key);
      }
    });

    desired.forEach((wanted, variantId) => {
      const have = current.get(variantId) || 0;
      if (wanted > have) {
        increases.push({ id: variantId, quantity: wanted - have });
      } else if (wanted < have) {
        // Prefer the line key over the variant id - a variant can occupy more
        // than one line, and a key targets exactly one.
        decreases.push({
          id: lineKeyByVariant.get(variantId) || variantId,
          quantity: wanted
        });
      }
    });

    // Lines present in Shopify but no longer in the bundle.
    current.forEach((have, variantId) => {
      if (!desired.has(variantId)) {
        decreases.push({
          id: lineKeyByVariant.get(variantId) || variantId,
          quantity: 0
        });
      }
    });

    // Reductions first, so a large box does not transiently exceed inventory.
    // change.js sets an absolute quantity, so repeated runs are idempotent.
    for (const change of decreases) {
      await fetch(`${prefix}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(change.id), quantity: change.quantity })
      });
    }

    // add.js is additive and is the call Shopify treats as a genuine add, so it
    // is what actually emits product_added_to_cart. Sending only the delta keeps
    // the event's quantity honest.
    if (increases.length > 0) {
      await fetch(`${prefix}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: increases })
      });
    }

    return true;
  } catch (error) {
    // Never let a tracking-side failure interfere with building a box. The
    // pre-checkout reconcile is the backstop for correctness.
    console.warn('Bundle cart sync failed (non-blocking):', error);
    return false;
  } finally {
    this._cartSyncInFlight = false;
    if (this._cartSyncQueued) {
      this._cartSyncQueued = false;
      this.requestCartSync();
    }
  }
}

// Runs immediately before a straight-to-checkout redirect. Idempotent: fires no
// requests when the cart already matches, which is the common case and is what
// keeps that path free of a duplicate AddToCart burst.
async reconcileCartForCheckout() {
  clearTimeout(this._cartSyncTimer);
  this._cartSyncQueued = false;

  // Wait for any sync already running instead of forcing the in-flight flag
  // false. Clearing the flag let a second sync start alongside the first; both
  // would read the same pre-write cart, both compute the same increases, and
  // both call add.js - doubling quantities. The match check below would have
  // caught it and fallen back to the rebuild, so no customer was ever going to
  // be overcharged, but relying on the last line of defence for something
  // avoidable is not good enough on a cart.
  if (this._cartSyncActive) {
    try {
      await this._cartSyncActive;
    } catch (error) {
      /* a failed sync is handled by the match check below */
    }
  }

  this._cartSyncActive = this.syncShopifyCart();
  return this._cartSyncActive;
}

// Escape hatch for the worst case: the Shopify cart holding something the
// bundle builder is not showing. Wipes builder state, localStorage and the real
// Shopify cart together, so the customer can always get back to a known-empty
// starting point without contacting support.
async clearEntireCart() {
  // Cancel pending work first so nothing re-populates behind the clear.
  clearTimeout(this._cartSyncTimer);
  this._cartSyncQueued = false;
  if (this._cartSyncActive) {
    try { await this._cartSyncActive; } catch (error) { /* ignore */ }
  }

  // Route removals through the normal change event so the product grid resets
  // its quantity inputs and data-active states rather than going stale.
  [...this.selectedItems].forEach((item) => {
    const detail = { productId: item.productId, quantity: 0 };
    if (item.isGiftCard) {
      detail.variantId = item.variantId;
      detail.properties = item.properties;
    }
    document.dispatchEvent(new CustomEvent('wiq:bundle:change', { detail }));
  });

  this.selectedItems = [];
  this.storage.set([]);
  sessionStorage.removeItem('bundle_gift_notes');

  // Cancel again - the dispatches above each scheduled a sync.
  clearTimeout(this._cartSyncTimer);
  this._cartSyncQueued = false;

  try {
    await fetch(`${this.cartRoutePrefix}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' }
    });
  } catch (error) {
    console.warn('Shopify cart clear failed:', error);
  }

  this.totalCount = 0;
  this.countWithGiftCards = 0;
  this.updateCheckoutButton();
  this.renderPrice();
  this.progressBar.updateProgress(0);
  this.updateUpsellText();
  this.render();
}

// True only when the live-synced cart is already checkout-ready as-is.
// Subscriptions need selling_plan and the __appstle-bb-* properties, and gift
// cards need their message properties; both are applied by the existing form
// POST rebuild, so those paths keep using it.
canCheckoutFromSyncedCart() {
  if (this.selectedItems.length === 0) return false;
  if (this.purchaseOptions && this.purchaseOptions.selectedOption === 'subscription') return false;
  if (this.selectedItems.some((item) => item.isGiftCard)) return false;
  return true;
}

// Cart attributes to stamp on the order when the customer accepted the seasonal
// upsell popup (set by pi-seasonal-upsell.js). A Shopify Flow keyed on
// _upsell_accepted turns this into the "Upsell" order tag; _upsell_items keeps a
// human-readable record of which flavors converted. Returns null when the popup
// was not accepted, so the normal checkout writes nothing extra.
seasonalUpsellCartAttributes() {
  if (!this._seasonalUpsellAccepted) return null;
  return {
    _upsell_accepted: 'true',
    _upsell_items: this._seasonalUpsellSummary || ''
  };
}

updateGiftCardOnlyVisibility() {
    // Check if cart contains ONLY gift cards
    const hasOnlyGiftCards = this.selectedItems.length > 0 && 
                            this.selectedItems.every(item => item.isGiftCard);
    
    // Elements to hide when only gift cards
    const elementsToHide = [
      '.pi-shopping-cart__actions',
      '.pi-cart-progress',
      '.pi-shopping-cart__total-prices'
    ];
    
    elementsToHide.forEach(selector => {
      const elements = this.querySelectorAll(selector);
      elements.forEach(el => {
        if (hasOnlyGiftCards) {
          el.style.display = 'none';
        } else {
          el.style.display = '';
        }
      });
    });
  }

render() {
  // Check if cart contains ONLY gift cards
  const hasOnlyGiftCards = this.selectedItems.length > 0 && 
                          this.selectedItems.every(item => item.isGiftCard);
  
  // Update or hide the item count based on cart contents
  this.itemsCountEl.forEach((el) => {
    if (hasOnlyGiftCards) {
      // Hide the count element when only gift cards
      el.style.display = 'none';
    } else {
      // Show the count element and update text
      el.style.display = '';
      el.innerHTML = `${this.totalCount} ${
        this.totalCount > 1 ? "Items" : "Item"
      }`;
    }
  });

  if (this.selectedItems.length == 0) {
    this.selectedItemsContainer.innerHTML = "";
    this.setAttribute("data-empty-state", "");
    return false;
  }
  this.removeAttribute("data-empty-state");
  
  var html = [];
  
  // Generate HTML for each item
  this.selectedItems.forEach((selectedItem, index) => {
    // Create a unique ID for this render
    const uniqueId = `item-${index}-${Date.now()}`;
    
    // Store gift card data on the item
    if (selectedItem.isGiftCard && selectedItem.item) {
      selectedItem.item.variantId = selectedItem.variantId;
      selectedItem.item.properties = selectedItem.properties;
    }
    
    // Pass the unique ID to getHtml
    html.push(selectedItem.item.getHtml(selectedItem.quantity, selectedItem.price, uniqueId));
  });
  
  this.selectedItemsContainer.innerHTML = html.join("");
  this.updateGiftCardOnlyVisibility();
}

attachSelectedItemListeners() {
  this.selectedItemsContainer.querySelectorAll('.selected-item-wrapper').forEach((wrapper) => {
    const index = parseInt(wrapper.dataset.selectedIndex);
    const selectedItem = this.selectedItems[index];
    
    if (!selectedItem) return;
    
    // Find the quantity input within this wrapper
    const quantityInput = wrapper.querySelector('wiq-quantity-input');
    const removeBtn = wrapper.querySelector('button[data-remove-item]');
    
    if (quantityInput) {
      // Remove any existing listeners to avoid duplicates
      const newInput = quantityInput.cloneNode(true);
      quantityInput.parentNode.replaceChild(newInput, quantityInput);
      
      // Create custom quantity input handler for this specific item
      const input = newInput.querySelector('input');
      const plusBtn = newInput.querySelector('button[name="plus"]');
      const minusBtn = newInput.querySelector('button[name="minus"]');
      
      // Handle direct input changes
      input.addEventListener('change', (e) => {
        const newQuantity = parseInt(e.target.value) || 0;
        this.updateSpecificItem(selectedItem, newQuantity);
      });
      
      // Handle plus button
      if (plusBtn) {
        plusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const currentValue = parseInt(input.value) || 0;
          const newValue = currentValue + 1;
          input.value = newValue;
          this.updateSpecificItem(selectedItem, newValue);
        });
      }
      
      // Handle minus button
      if (minusBtn) {
        minusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const currentValue = parseInt(input.value) || 0;
          const newValue = Math.max(0, currentValue - 1);
          input.value = newValue;
          this.updateSpecificItem(selectedItem, newValue);
        });
      }
    }
    
    // Handle remove button
    if (removeBtn) {
      const newRemoveBtn = removeBtn.cloneNode(true);
      removeBtn.parentNode.replaceChild(newRemoveBtn, removeBtn);
      
      newRemoveBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.updateSpecificItem(selectedItem, 0);
      });
    }
  });
}

updateSpecificItem(selectedItem, newQuantity) {
  // Dispatch event with all necessary data for gift cards
  const eventDetail = {
    productId: selectedItem.productId,
    quantity: newQuantity,
  };
  
  // Include variant and properties for gift cards
  if (selectedItem.isGiftCard) {
    eventDetail.variantId = selectedItem.variantId;
    eventDetail.properties = selectedItem.properties;
  }
  
  document.dispatchEvent(
    new CustomEvent("wiq:bundle:change", {
      detail: eventDetail
    })
  );
}

 renderPrice() {
    this.subscriptionPriceEls = document.querySelectorAll("[data-subscription-price]");
    this.oneTimePriceEls = document.querySelectorAll("[data-one-time-price]");

    // helpers
    const roundCents = (x) => Math.round(x + Number.EPSILON); // nearest cent
    const floorCents = (x) => Math.floor(x + Number.EPSILON); // truncate to cent

    // Volume discount: PER UNIT, floored to the cent.
    //
    // Shopify rounds percentage discounts differently depending on the discount
    // TYPE, which is easy to get wrong because both look identical in the API as
    // DiscountAutomaticBasic:
    //
    //   "Amount off order"    -> order level. floor(subtotal x pct), then the
    //                            total is distributed across lines, shaving odd
    //                            cents off individual lines so the parts sum
    //                            back to it. Seen on #40363 (225.36 -> 11.26,
    //                            allocations 5.63 + 5.63) and #40357 (253.53 ->
    //                            12.67, allocations 1.41 x7 + 1.40 x2).
    //
    //   "Amount off products" -> per unit. floor(unitPrice x pct) per unit, then
    //                            x quantity. This is what the store runs now:
    //                            "5% Volume Discount" / "10% Volume Discount",
    //                            scoped to the Bundle collection.
    //
    // Confirmed against a live subscription checkout of 24 units at 9.39:
    //   unit after the 15% selling plan  798c
    //   floor(798 x 5%) = floor(39.9)    39c per unit
    //   21 x 39 = 819c and 3 x 39 = 117c  -> matches the -8.19 / -1.17 shown
    //   24 x (798 - 39) = 18216c          -> matches the 182.16 charged
    //
    // The order-level version of this calculation gave 9.57 against Shopify's
    // 9.36 - a 21c gap - which is why it is not used. Do not "simplify" this to
    // a single percentage of the subtotal; the per-unit truncation is the point,
    // and it only matches while the discounts remain product-scoped.
    const perUnitAfterVolume = (unitCents) => {
      if (!hasVolume) return unitCents;
      return unitCents - floorCents(unitCents * (volumeDiscount.value / 100));
    };

    // reset/vars (all cents)
    this.originalSubTotal = 0;
    this.subTotal = 0;
    this.totalPrice = 0;

    let shippingCost = 0;
    let giftCardTotal = 0;

    let volumeDiscountTotalOneTime = 0;
    let volumeDiscountTotalSub = 0;
    this.subscriptionDiscount = 0;

    const volumeDiscount = this.loop.getCurrentDiscount(this.totalCount); 
    const hasVolume = volumeDiscount && typeof volumeDiscount.value === "number" && volumeDiscount.value > 0;
    const subPct = (this.subscriptionPercentage || 0); // e.g., 0.15 means 15%

    // Original subtotal (no discounts)
    this.originalSubTotal = this.selectedItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

    // Gift card subtotal (no discounts, no changes)
    giftCardTotal = this.selectedItems.reduce((acc, item) => item.isGiftCard ? acc + (item.price * item.quantity) : acc, 0);

    // Subtotals after discount paths
    let subtotalAfterVolume_OneTime = 0; 
    let subtotalAfterBoth_Sub = 0;       

    this.selectedItems.forEach((item) => {
      const unitCents = item.price; // cents
      const qty = item.quantity;

      // Gift cards are never discounted, and the discounts are now scoped to the
      // Bundle collection, which excludes them - so builder and Shopify agree.
      if (item.isGiftCard) {
        subtotalAfterVolume_OneTime += unitCents * qty;
        subtotalAfterBoth_Sub += unitCents * qty;
        return;
      }

      // ONE-TIME: volume tier straight off the list price, per unit.
      const unitAfterVolume_OneTime = perUnitAfterVolume(unitCents);
      volumeDiscountTotalOneTime += (unitCents - unitAfterVolume_OneTime) * qty;
      subtotalAfterVolume_OneTime += unitAfterVolume_OneTime * qty;

      // SUBSCRIPTION, in Shopify's order of operations:
      // 1. the selling plan's own discount, applied to the unit price and
      //    rounded to the NEAREST cent (9.39 x 0.85 = 7.9815 -> 7.98, confirmed
      //    on live checkouts) - this one is a pricing policy, not a discount
      //    application, so it rounds rather than truncates
      let unitAfterSub = unitCents;
      if (subPct > 0) {
        unitAfterSub = roundCents(unitCents * (1 - subPct));
        this.subscriptionDiscount += (unitCents - unitAfterSub) * qty;
      }

      // 2. then the volume tier against that reduced unit price, floored
      const unitAfterBoth_Sub = perUnitAfterVolume(unitAfterSub);
      volumeDiscountTotalSub += (unitAfterSub - unitAfterBoth_Sub) * qty;
      subtotalAfterBoth_Sub += unitAfterBoth_Sub * qty;
    });

    // Shipping
    const hasOnlyGiftCards = this.selectedItems.length > 0 && 
                             this.selectedItems.every(item => item.isGiftCard);
    
    if (hasOnlyGiftCards || this.shippingFee == 0 || this.totalCount >= this.shippingThreshold) {
      this.shippingEl.innerHTML = "Free";
      shippingCost = 0;
    } else {
      shippingCost = this.shippingFee;
      this.shippingEl.innerHTML = Shopify.formatMoney(this.shippingFee);
    }

    // Totals
    const oneTimeTotal = subtotalAfterVolume_OneTime + shippingCost;  // cents
    const subscriptionTotal = subtotalAfterBoth_Sub + shippingCost;   // cents

    // Maintain legacy fields
    this.subTotal = subtotalAfterVolume_OneTime; 
    this.totalPrice = oneTimeTotal;

    // ---- Render subscription price blocks ----
    if (subPct > 0) {
      if (this.purchaseOptions.selectedOption == "subscription") {
        this.subscriptionDiscountEl.innerHTML = Shopify.formatMoney(this.subscriptionDiscount);
      } else {
        this.subscriptionDiscountEl.innerHTML = Shopify.formatMoney(0);
      }

      this.subscriptionPriceEls.forEach((el) => {
        const hasDiscounts = (volumeDiscountTotalSub > 0) || (this.subscriptionDiscount > 0);
        if (hasDiscounts) {
          const originalTotal = this.originalSubTotal + shippingCost;
          el.innerHTML = `
            <div class="price-stack">
              <div class="original-price">${Shopify.formatMoney(originalTotal)}</div>
              <div class="discounted-price">${Shopify.formatMoney(subscriptionTotal)}</div>
            </div>
          `;
        } else {
          el.innerHTML = Shopify.formatMoney(subscriptionTotal);
        }
      });
    }

    // ---- Render one-time price blocks ----
    this.oneTimePriceEls.forEach((el) => {
      if (volumeDiscountTotalOneTime > 0) {
        const originalTotal = this.originalSubTotal + shippingCost;
        el.innerHTML = `
          <div class="price-stack">
            <div class="original-price">${Shopify.formatMoney(originalTotal)}</div>
            <div class="discounted-price">${Shopify.formatMoney(oneTimeTotal)}</div>
          </div>
        `;
      } else {
        el.innerHTML = Shopify.formatMoney(oneTimeTotal);
      }
    });

    // Subtotal (original, no discounts)
    this.subtotalEl.innerHTML = Shopify.formatMoney(this.originalSubTotal);

    // Volume discount line
    const volumeForSelected =
      (this.purchaseOptions.selectedOption == "subscription")
        ? volumeDiscountTotalSub
        : volumeDiscountTotalOneTime;

    this.volumeDiscountEl.innerHTML = Shopify.formatMoney(volumeForSelected);

    // Checkout total block
    const currentTotal =
      (this.purchaseOptions.selectedOption == "subscription")
        ? subscriptionTotal
        : oneTimeTotal;

    const hasAnyDiscounts =
      (this.purchaseOptions.selectedOption == "subscription")
        ? (volumeDiscountTotalSub > 0 || this.subscriptionDiscount > 0)
        : (volumeDiscountTotalOneTime > 0);

    if (hasAnyDiscounts) {
      const originalTotal = this.originalSubTotal + shippingCost;
      this.checkoutTotalPrice.innerHTML = `
        <div class="price-stack">
          <div class="original-price">${Shopify.formatMoney(originalTotal)}</div>
          <div class="discounted-price">${Shopify.formatMoney(currentTotal)}</div>
        </div>
      `;
    } else {
      this.checkoutTotalPrice.innerHTML = Shopify.formatMoney(currentTotal);
    }

    this.render();
  }


  updateUpsellText() {
    // Check if cart contains ONLY gift cards
    const hasOnlyGiftCards = this.selectedItems.length > 0 && 
                            this.selectedItems.every(item => item.isGiftCard);
    
    // Different messaging for gift-card-only carts
    if (hasOnlyGiftCards) {
      this.upsellText.forEach((el) => {
        el.innerHTML = "Gift cards ready for checkout";
      });
      return;
    }
    
    // Original upsell logic for regular items
    var volumeDiscount = 0;
    const purchaseOption = this.purchaseOptions.selectedOption;
    var closestDiscount = this.loop.getClosestDiscount(this.totalCount, true);
    var left = closestDiscount.minCartQuantity - this.totalCount;
    if (left == 0) {
      closestDiscount = this.loop.getClosestDiscount(this.totalCount + 1, true);
      left = closestDiscount.minCartQuantity - this.totalCount;
    }
    if (closestDiscount.conditionType != "shipping") {
      volumeDiscount = this.subTotal * (closestDiscount.value / 100);
    }
    var message = "";

    if (this.totalCount < this.minimumLimit && this.minimumLimit > 0) {
      message = `Add ${
        this.minimumLimit - this.totalCount
      } more items to checkout.`;
    } else if (this.totalCount >= this.loop.highestThreshold) {
      message = `You've reached the maximum discount tier!`;
    } else if (closestDiscount.conditionType == "shipping") {
      message = `Add ${
        closestDiscount.minCartQuantity - this.totalCount
      } more items to get Free Shipping`;
    } else if (
      this.subscriptionPercentage > 0 &&
      purchaseOption == "subscription"
    ) {
      message = `Add ${left} more items to save ${Shopify.formatMoney(
        volumeDiscount + this.subscriptionDiscount
      )} with a subscription!`;
    } else {
      message = `Add ${left} more items to save ${Shopify.formatMoney(
        volumeDiscount
      )}!`;
    }

    this.upsellText.forEach((el) => {
      el.innerHTML = message;
    });
}

  onPurchaseOptionsChange() {
    this.renderPrice();
    this.updateUpsellText();
  }

  onViewBundleCartClick(event) {
    event.preventDefault();
    this.parentElement.toggleAttribute("data-mobile-cart-drawer");
  }

  updateCheckoutButton() {
    // Check if cart contains ONLY gift cards
    const hasOnlyGiftCards = this.selectedItems.length > 0 && 
                            this.selectedItems.every(item => item.isGiftCard);
    
    // Gift-card-only carts can always check out
    if (hasOnlyGiftCards) {
      this.checkoutModalOpener.removeAttribute("disabled");
      this.checkoutModalOpener.removeAttribute("data-insufficient-items");
      
      const innerTextSpan = this.checkoutButtonText.querySelector(
        ".pi-checkout-button__text-text"
      );
      if (innerTextSpan) {
        innerTextSpan.textContent = "Check Out Now";
      } else {
        this.checkoutButtonText.innerHTML = `<span class="pi-checkout-button__text-text">Check Out Now</span>`;
      }
      return;
    }
    
    // Regular logic for mixed carts or non-gift-card carts
    if (this.totalCount < this.minimumLimit && this.minimumLimit > 0) {
      const itemsNeeded = this.minimumLimit - this.totalCount;
      this.checkoutModalOpener.setAttribute("disabled", "");
      this.checkoutModalOpener.setAttribute("data-insufficient-items", "");
      this.checkoutButtonText.textContent = `Add ${itemsNeeded} more item${
        itemsNeeded > 1 ? "s" : ""
      } to check out`;
    } else {
      this.checkoutModalOpener.removeAttribute("disabled");
      this.checkoutModalOpener.removeAttribute("data-insufficient-items");

      const innerTextSpan = this.checkoutButtonText.querySelector(
        ".pi-checkout-button__text-text"
      );
      if (innerTextSpan) {
        innerTextSpan.textContent = "Check Out Now";
      } else {
        this.checkoutButtonText.innerHTML = `<span class="pi-checkout-button__text-text">Check Out Now</span>`;
      }
    }
  }


async addBundleToCart(type, modal) {
    // Cancel any debounced cart sync before clearing and rebuilding. Without
    // this, a sync scheduled by a just-before-checkout click could fire in the
    // window between cart/clear.js and form.submit() and re-add lines with no
    // selling_plan and no __appstle-bb-* properties - which for a subscription
    // is exactly the state where Appstle's volume discount fails to apply.
    clearTimeout(this._cartSyncTimer);
    this._cartSyncQueued = false;

    const orderNoteInput = modal.querySelector('[data-gift-note-input]');
    const orderNote = orderNoteInput ? orderNoteInput.value.trim() : '';

    const hasOnlyGiftCards = this.selectedItems.length > 0 && 
                             this.selectedItems.every(item => item.isGiftCard);
    
    if (hasOnlyGiftCards) {
      type = "one-time";
    }
    
    var selling_plan = null;
    if (type == "subscription") {
      selling_plan = this.subscriptionFrequency.selectedValue;
      if (!selling_plan || !this.subscriptionConsent.checked) {
        modal.hide();
        this.purchaseOptions.switchOption("subscription");
        this.subscriptionFrequency._focus();
        return;
      }
    } else {
      this.purchaseOptions.switchOption("one-time");
    }

    modal.currentBtn.setAttribute("data-loading", "");
    modal.currentBtn.setAttribute("disabled", "");
    
    try {
      const giftNotes = JSON.parse(sessionStorage.getItem('bundle_gift_notes') || '[]');

      // --- DYNAMIC PRICING: SEND EXACT APPSTLE PAYLOAD ---
      const allItems = this.selectedItems.map((item) => {
        const properties = item.properties || {};
        
        // Add Gift Notes
        const giftNote = giftNotes.find(note => note.productId == item.productId);
        if (giftNote && giftNote.note) {
          properties['Gift Note'] = giftNote.note;
        }
        
        const vId = parseInt(item.variantId || (item.item && item.item.dataset ? item.item.dataset.variantId : item.productId));
        const qty = parseInt(item.quantity);

        // --- INJECT EXACT APPSTLE KEYS FROM NATIVE PAYLOAD ---
        // Only for subscription orders - Appstle's bundle discount/subscription properties
        // should never be attached to one-time purchase line items.
        if (!item.isGiftCard && type === "subscription") {
          properties['__appstle-bb-name'] = "My Frozen Garden Box"; // Double underscore
          properties['__appstle-bb-variants'] = `${vId}:${qty}`;    // Double underscore (Triggers the discount!)
          properties['_appstle-bb-id'] = 'lx8ilmzr';                // Single underscore

          // Appstle also passes the SKU. We'll try to grab it from your dataset, or fallback to the product handle
          properties['_appstle-bb-product-sku'] = (item.item && item.item.dataset.sku) ? item.item.dataset.sku : item.handle;
        }

        return {
          id: vId,
          quantity: qty,
          selling_plan: item.isGiftCard ? null : (selling_plan || null),
          properties: Object.keys(properties).length > 0 ? properties : null,
        };
      });

      // 1. Clear cart
      await fetch((window.Shopify.routes && window.Shopify.routes.root + 'cart/clear.js') || '/cart/clear.js', { method: 'POST' });

      // Set order note (and the seasonal-upsell cart attributes) while the cart
      // is empty - this fetch is not Cloudflare-blocked, and cart/clear.js above
      // drops line items only, so writing here guarantees the attributes reach
      // the order for the Shopify Flow that applies the "Upsell" tag.
      const upsellAttributes = this.seasonalUpsellCartAttributes();
      if (orderNote || upsellAttributes) {
        const updateBody = { note: orderNote };
        if (upsellAttributes) updateBody.attributes = upsellAttributes;
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateBody)
        });
      }

      sessionStorage.setItem('bundle_checkout_attempted', 'true');
      sessionStorage.setItem('bypass_cart_redirect', 'true');
      sessionStorage.removeItem('bundle_gift_notes');

      // Use a form POST to /cart/add instead of fetch to /cart/add.js.
      // A full browser navigation bypasses Cloudflare's bot challenge on AJAX POSTs.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = window.routes.cart_add_url;
      form.style.display = 'none';

      const addField = (name, value) => {
        if (value === null || value === undefined) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      addField('return_to', '/checkout');

      allItems.forEach((item, index) => {
        addField(`items[${index}][id]`, item.id);
        addField(`items[${index}][quantity]`, item.quantity);
        if (item.selling_plan) addField(`items[${index}][selling_plan]`, item.selling_plan);
        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            addField(`items[${index}][properties][${key}]`, value);
          });
        }
      });

      document.body.appendChild(form);
      form.submit();

    } catch (error) {
      console.error("CRITICAL CHECKOUT ERROR:", error);
      modal.currentBtn.removeAttribute("data-loading");
      modal.currentBtn.removeAttribute("disabled");
      alert("There was an issue adding your box to the cart. Please try again.");
    }
  }

  onBundleClear() {
    this.selectedItems = [];
    this.totalCount = 0;
    this.render();
  }
} // <-- Closes the WIQBundleBuilder class properly

customElements.define("wiq-bundle-builder", WIQBundleBuilder);

function formatIngredientDots() {
  document
    .querySelectorAll(".product-card__ingredients")
    .forEach((container) => {
      const spans = Array.from(container.querySelectorAll("span"));
      let currentLineTop = null;

      spans.forEach((span, i) => {
        const spanTop = span.getBoundingClientRect().top;

        // If it's the first span or a new line
        if (currentLineTop === null || spanTop !== currentLineTop) {
          // Replace middle dot on previous span if it exists
          if (i > 0) {
            spans[i - 1].innerHTML = spans[i - 1].textContent.replace(
              /·\s*$/,
              "\u00A0\u00A0"
            );
          }
          currentLineTop = spanTop;
        }

        // Ensure current span has a middle dot (unless it's last)
        if (!span.textContent.trim().endsWith("·")) {
          span.innerHTML = span.textContent.trim() + " · ";
        }
      });

      if (spans.length > 0) {
        spans[spans.length - 1].innerHTML = spans[
          spans.length - 1
        ]?.textContent?.replace(/·\s*$/, "\u00A0\u00A0");
      }
    });
}

// Run on DOM ready and on window resize
window.addEventListener("DOMContentLoaded", formatIngredientDots);
window.addEventListener("resize", formatIngredientDots);

// ✅ Not Needed
// // More specific event delegation to avoid conflicts
// document.addEventListener('click', function(event) {
//   // More specific selector to avoid conflicts
//   if (event.target.closest('wiq-predefined-bundle') &&
//       event.target.matches('button.pi-predefined-bundle__block-button')) {
//     event.preventDefault();
//     event.stopPropagation(); // Prevent other handlers from firing

//     const wiqElement = event.target.closest('wiq-predefined-bundle');
//     const scriptTag = wiqElement.querySelector('script[type="application/json"]');

//     if (scriptTag) {
//       try {
//         const data = JSON.parse(scriptTag.textContent);
//         const predefinedProducts = data.predefined_products || [];

//         // Get the current bundle builder
//         const bundleBuilder = document.querySelector('wiq-bundle-builder');
//         if (bundleBuilder) {
//           // Add products with cumulative quantities
//           predefinedProducts.forEach(product => {
//             if (product.product_id && product.quantity > 0) {
//               // Find the existing item in selectedItems
//               const existingItem = bundleBuilder.selectedItems.find(
//                 item => item.productId == product.product_id.toString()
//               );

//               // Calculate new quantity (existing + predefined bundle quantity)
//               const currentQuantity = existingItem ? existingItem.quantity : 0;
//               const newQuantity = currentQuantity + product.quantity;

//               // Dispatch the event with the cumulative quantity
//               document.dispatchEvent(
//                 new CustomEvent('wiq:bundle:change', {
//                   detail: {
//                     productId: product.product_id.toString(),
//                     quantity: newQuantity,
//                     fromPredefinedBundle: true
//                   }
//                 })
//               );
//             }
//           });
//         }
//       } catch (error) {
//         console.error('Error parsing predefined products:', error);
//       }
//     }
//   }
// });

// Remove any existing event listeners by cloning and replacing elements
// document.querySelectorAll("wiq-predefined-bundle button").forEach((button) => {
//   const newButton = button.cloneNode(true);
//   button.parentNode.replaceChild(newButton, button);
// });

// Single, controlled event listener for predefined bundles
function handlePredefinedBundleClick(event) {
  // Prevent any other handlers from firing
  event.preventDefault();
  event.stopImmediatePropagation();

  // Add a flag to prevent multiple rapid clicks
  if (event.target.hasAttribute("data-processing")) {
    return;
  }

  event.target.setAttribute("data-processing", "true");

  // Remove the processing flag after a short delay
  setTimeout(() => {
    event.target.removeAttribute("data-processing");
  }, 500);

  const wiqElement = event.target.closest("wiq-predefined-bundle");
  const scriptTag = wiqElement.querySelector('script[type="application/json"]');

  if (scriptTag) {
    try {
      const data = JSON.parse(scriptTag.textContent);
      const predefinedProducts = data.predefined_products || [];

      // Get the current bundle builder
      const bundleBuilder = document.querySelector("wiq-bundle-builder");
      if (bundleBuilder) {
        // Add products with cumulative quantities
        predefinedProducts.forEach((product) => {
          if (product.product_id && product.quantity > 0) {
            // Find the existing item in selectedItems
            const existingItem = bundleBuilder.selectedItems.find(
              (item) => item.productId == product.product_id.toString()
            );

            // Calculate new quantity (existing + predefined bundle quantity)
            const currentQuantity = existingItem ? existingItem.quantity : 0;
            const newQuantity = currentQuantity + product.quantity;

            // Dispatch the event with the cumulative quantity
            document.dispatchEvent(
              new CustomEvent("wiq:bundle:change", {
                detail: {
                  productId: product.product_id.toString(),
                  quantity: newQuantity,
                  fromPredefinedBundle: true,
                },
              })
            );
          }
        });
      }
    } catch (error) {
      console.error("Error parsing predefined products:", error);
    }
  }
}

// Direct event listener attachment
// document.querySelectorAll("wiq-predefined-bundle button").forEach((button) => {
//   button.addEventListener("click", handlePredefinedBundleClick);
// });

// --- Predefined Bundle "View More Info" popup ---
// Reads the per-bundle product JSON already embedded server-side (title/product_type/
// nutrition_image) and renders it into that section's own modal as expandable accordion rows.
// No fetch needed - everything is already on the page at load time.
function openBundleInfoModal(button) {
  const section = button.closest("section");
  if (!section) return;

  // The modal gets moved to <body> (see below) the first time it's opened,
  // so it can no longer be found via section.querySelector on later opens -
  // look it up by the section's stable Shopify section id instead, which
  // doesn't depend on the modal's current DOM parent.
  const sectionId = section.getAttribute("data-bundle-section-id");
  const modal = document.querySelector(
    `[data-bundle-info-modal="${sectionId}"]`
  );
  const targetId = button.getAttribute("data-bundle-info-target");
  const source = section.querySelector(
    `[data-bundle-info-source="${targetId}"]`
  );
  if (!modal || !source) return;

  let data;
  try {
    data = JSON.parse(source.textContent);
  } catch (error) {
    console.error("Error parsing bundle info data:", error);
    return;
  }

  const titleEl = modal.querySelector("[data-bundle-info-title]");
  const listEl = modal.querySelector("[data-bundle-info-list]");
  if (titleEl) titleEl.textContent = data.name || "";

  if (listEl) {
    listEl.innerHTML = (data.products || [])
      .map((product) => {
        const nutritionMarkup = product.nutrition_image
          ? `<a href="${product.nutrition_image}" target="_blank" rel="noopener" class="pi-bundle-info-modal__item-image-link"><img src="${product.nutrition_image}" alt="Nutrition Facts for ${escapeHtml(
              product.title
            )}" loading="lazy"></a>`
          : `<p class="pi-bundle-info-modal__item-empty">Nutrition facts coming soon.</p>`;

        const displayTitle = product.product_type
          ? `${product.title} ${product.product_type}`
          : product.title;

        return `
          <details class="pi-bundle-info-modal__item">
            <summary>
              <span class="pi-bundle-info-modal__item-title">${escapeHtml(displayTitle)}</span>
              ${
                product.quantity
                  ? `<span class="pi-bundle-info-modal__item-qty">x${escapeHtml(
                      product.quantity
                    )}</span>`
                  : ""
              }
            </summary>
            <div class="pi-bundle-info-modal__item-content">${nutritionMarkup}</div>
          </details>
        `;
      })
      .join("");
  }

  // This section has the theme's .isolate utility class (position: relative;
  // z-index: 0), which creates its own stacking context - no z-index on the
  // modal, however high, can ever paint above sibling sections (like the
  // cart/product listing) while it stays nested inside that context. Moving
  // it to be a direct child of <body> escapes that trap entirely.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  positionBundleInfoModal(modal);
  modal.hidden = false;
  document.body.style.overflow = "hidden";
}

// The modal overlay covers the full viewport (position: fixed; inset: 0),
// but the site's sticky header + announcement bar sit on top of it and can
// cover the modal card's title/close button if the card starts too close to
// the true viewport top. Measures the real, currently-visible sticky header
// height (varies with the announcement bar and breakpoint) and offsets the
// modal below it, rather than guessing a fixed pixel value.
function positionBundleInfoModal(modal) {
  const inner = modal.querySelector(".pi-bundle-info-modal__inner");
  if (!inner) return;

  const stickyHeader = document.querySelector(".shopify-section-header-sticky");
  const headerBottom = stickyHeader
    ? stickyHeader.getBoundingClientRect().bottom
    : 0;
  const topOffset = Math.max(headerBottom, 0) + 16;

  inner.style.marginTop = `${topOffset}px`;
  inner.style.maxHeight = `calc(100vh - ${topOffset + 24}px)`;
}

function closeBundleInfoModal(modal) {
  modal.hidden = true;
  document.body.style.overflow = "";
}

function escapeHtml(value) {
  const div = document.createElement("div");
  div.textContent = value == null ? "" : String(value);
  return div.innerHTML;
}

document.addEventListener("click", (event) => {
  const openBtn = event.target.closest("[data-bundle-info-open]");
  if (openBtn) {
    openBundleInfoModal(openBtn);
    return;
  }

  const closeTrigger = event.target.closest("[data-bundle-info-close]");
  if (closeTrigger) {
    const modal = closeTrigger.closest("[data-bundle-info-modal]");
    if (modal) closeBundleInfoModal(modal);
  }
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  document
    .querySelectorAll("[data-bundle-info-modal]:not([hidden])")
    .forEach((modal) => closeBundleInfoModal(modal));
});